// Translation system for Yarnboard

export interface Translations {
  // Navigation
  nav: {
    home: string;
    documents: string;
    visualize: string;
    discover: string;
    settings: string;
    addNote: string;
    addDocument: string;
    tagManagement: string;
    importDocuments: string;
    upgrade: string;
    sharedWithMe: string;
    notes: string;
    storyboards: string;
    storyboardsTab: string;
    shared: string;
    about: string;
    feedback: string;
    addFolder: string;
    addStoryboard: string;
  };
  // Home page
  home: {
    searchPlaceholder: string;
    loading: string;
    noEntries: string;
    noDocuments: string;
    createFirstNote: string;
    createFirstDocument: string;
    selectEntries: string;
    cancel: string;
    next: string;
    selected: string;
    createDocumentFromNotes: string;
    newFolder: string;
    untitledNote: string;
    subfolderNamePrompt: string;
    folderCreateFailed: string;
    searchDocumentsPlaceholder: string;
    searchNotesPlaceholder: string;
    includeRelated: string;
    includeRelatedHint: string;
    createNewFolder: string;
    noNotes: string;
    tryDifferentSearch: string;
    clickAddNote: string;
    relatedNotes: string;
    closeMatch: string;
    matchedTag: string;
    connectedBy: string;
    inPdf: string;
    loadingMoreNotes: string;
    importLocal: string;
    importLocalDesc: string;
    importGoogleDocs: string;
    importGoogleDocsDesc: string;
    suggestionsTags: string;
    suggestionsRecent: string;
    removeFromHistory: string;
    clearSearch: string;
  };
  // Note Card
  note: {
    edit: string;
    delete: string;
    viewImage: string;
    tags: string;
    links: string;
    createdOn: string;
    clickToCopy: string;
    clickToOpenPdf: string;
    openPdf: string;
    downloadPdf: string;
    seeThreads: string;
    viewMore: string;
    close: string;
    confirmDelete: string;
    imageCopied: string;
    textCopied: string;
    mediaAlt: string;
    loadingImage: string;
    noImageFile: string;
    noImageUrl: string;
    fullSizeAlt: string;
  };
  // Document
  document: {
    open: string;
    delete: string;
    rename: string;
    sources: string;
    lastModified: string;
    createdOn: string;
    deleteDocument: string;
    confirmDeleteDocument: string;
    selectedCount: string;
    clearSelection: string;
    dragSelectionHint: string;
    deleteSelected: string;
    confirmDeleteSelected: string;
  };
  // Folder
  folder: {
    createFolder: string;
    newFolder: string;
    folderName: string;
    enterFolderName: string;
    create: string;
    creating: string;
    cancel: string;
    delete: string;
    deleteFolder: string;
    cannotDeleteWithDocs: string;
    confirmDelete: string;
    confirmDeleteMessage: string;
    noDocuments: string;
    dragDropHint: string;
    expandFolder: string;
    collapseFolder: string;
    confirmDeleteFolder: string;
    folderHolds: string;
    documentsMoveToTop: string;
    subfoldersRemoved: string;
    alsoDeleteDocuments: string;
    alsoDeleteDocumentsHint: string;
    typeToConfirm: string;
    deleting: string;
    documentOne: string;
    documentMany: string;
    subfolderOne: string;
    subfolderMany: string;
  };
  // Tag Management
  tagManagement: {
    title: string;
    searchTags: string;
    sortBy: string;
    nameAZ: string;
    nameZA: string;
    mostUsed: string;
    leastUsed: string;
    color: string;
    textColor: string;
    selected: string;
    mergeSelected: string;
    deleteSelected: string;
    totalTags: string;
    showing: string;
    totalEntries: string;
    entries: string;
    edit: string;
    editTag: string;
    name: string;
    tagName: string;
    saveChanges: string;
    cancel: string;
    mergeTags: string;
    mergeInto: string;
    confirmMerge: string;
    confirmMergeMessage: string;
    cannotUndo: string;
    confirmDelete: string;
    confirmDeleteMessage: string;
    willRemoveTags: string;
    deleteTags: string;
    selectAtLeast2: string;
    selectTagsToDelete: string;
    loadingTags: string;
    enterTagName: string;
    preview: string;
    failedToUpdate: string;
    failedToMerge: string;
    failedToDelete: string;
  };
  // Settings
  settings: {
    title: string;
    account: string;
    appearance: string;
    language: string;
    help: string;
    about: string;
    // Account tab
    accountInfo: string;
    name: string;
    displayName: string;
    enterName: string;
    updateName: string;
    email: string;
    emailLabel: string;
    currentEmail: string;
    changeEmail: string;
    newEmail: string;
    enterNewEmail: string;
    updateEmail: string;
    updating: string;
    password: string;
    changePassword: string;
    currentPassword: string;
    enterCurrentPassword: string;
    newPassword: string;
    enterNewPassword: string;
    confirmPassword: string;
    confirmNewPassword: string;
    updatePassword: string;
    subscription: string;
      subscriptionPlaceholder: string;
      logout: string;
      logoutButton: string;
      // Appearance tab
      selectTheme: string;
    darkTheme: string;
    lightTheme: string;
    // Language tab
    selectLanguage: string;
    english: string;
    spanish: string;
    portuguese: string;
    // Help tab
    helpSupport: string;
    searchHelp: string;
    searchButton: string;
    helpPlaceholder: string;
    // Feedback
    feedbackTitle: string;
    feedbackDesc: string;
    feedbackTypeBug: string;
    feedbackTypeIdea: string;
    feedbackTypeOther: string;
    feedbackPlaceholder: string;
    feedbackSend: string;
    feedbackSending: string;
    feedbackThanks: string;
    feedbackError: string;
    // About tab
    aboutApp: string;
    version: string;
    aboutDescription: string;
    aboutPlaceholder: string;
    // Danger zone
    dangerZone: string;
    deactivateAccount: string;
    deactivateWarning: string;
    deactivate: string;
    deleteAccount: string;
    deleteWarning: string;
    deleteAccountButton: string;
    // Messages
    emailUpdated: string;
    nameUpdated: string;
    passwordUpdated: string;
    passwordChanged: string;
    themeUpdated: string;
    languageUpdated: string;
    passwordMismatch: string;
    passwordTooShort: string;
    updateFailed: string;
    failedToUpdate: string;
    failedToLoad: string;
    failedToChange: string;
    loading: string;
    currentPasswordPrompt: string;
    confirmDeactivate: string;
    failedToDeactivate: string;
    deletePrompt: string;
    failedToDeleteAccount: string;
    moreThemesSoon: string;
  };
  // Add Note Modal
  addNote: {
    title: string;
    cancel: string;
    remember: string;
    content: string;
    contentPlaceholder: string;
    tags: string;
    tagsPlaceholder: string;
    links: string;
    addLink: string;
    linkUrl: string;
    linkTitle: string;
    image: string;
    uploadImage: string;
    pasteImage: string;
    error: string;
    aiSuggest: string;
    generatingTags: string;
    tagSuggestionFailed: string;
    noTextInImage: string;
    aiOffNeedsTag: string;
    summarizeVideo: string;
    summarizeVideoDisabled: string;
    summarizingVideo: string;
    videoSummaryHeading: string;
    videoSummaryFailed: string;
    saving: string;
    pasteImageFailed: string;
    loadNoteFailed: string;
    unsupportedFile: string;
    fileFailed: string;
    ocrFailed: string;
    addContentFirst: string;
    contentRequired: string;
    tagRequiredFree: string;
    tagRequired: string;
    saveFailed: string;
    addAttachment: string;
    extractText: string;
    addSourceUrls: string;
    aiInfo: string;
    codeBlockHint: string;
    equation: string;
    equationHint: string;
    attachedAlt: string;
    removeImage: string;
    removePdf: string;
    codeLanguage: string;
    latexPrompt: string;
    latexInsertPrompt: string;
  };
  // Edit Note Modal
  editNote: {
    title: string;
    update: string;
  };
  // Add Document Modal
  addDocument: {
    title: string;
    documentName: string;
    documentNamePlaceholder: string;
    create: string;
    creating: string;
    cancel: string;
    nameRequired: string;
    failedToCreate: string;
  };
  // Document Editor
  editor: {
    untitled: string;
    saving: string;
    saved: string;
    unsaved: string;
    share: string;
    info: string;
    back: string;
    convertToEntry: string;
    convertToFootnote: string;
    // Menu items
    file: string;
    edit: string;
    insert: string;
    format: string;
    view: string;
    tools: string;
    help: string;
    print: string;
    undo: string;
    redo: string;
    selectAll: string;
    bold: string;
    italic: string;
    underline: string;
    strikethrough: string;
    textColor: string;
    highlightColor: string;
    fontSize: string;
    decreaseFontSize: string;
    increaseFontSize: string;
    heading: string;
    heading1: string;
    heading2: string;
    heading3: string;
    heading4: string;
    heading5: string;
    heading6: string;
    paragraph: string;
    bulletList: string;
    numberedList: string;
    checkList: string;
    alignLeft: string;
    alignCenter: string;
    alignRight: string;
    alignJustify: string;
    indent: string;
    outdent: string;
    link: string;
    image: string;
    table: string;
    horizontalRule: string;
    blockquote: string;
    codeBlock: string;
    clearFormatting: string;
    // Version history
    builtFrom: string;
    sourcesChanged: string;
    sourceChanged: string;
    writerNote: string;
    writerNoteHint: string;
    versionHistory: string;
    versionHistoryIntro: string;
    versionsLoading: string;
    noVersionsYet: string;
    versionWords: string;
    restoreVersion: string;
    restoreConfirm: string;
    restoreFailed: string;
    loadingDocument: string;
    loadFailed: string;
    saveFailed: string;
    backToEntryForm: string;
    importMenu: string;
    importLocalComputer: string;
    download: string;
    downloadPdf: string;
    downloadWord: string;
    downloadRichText: string;
    downloadWebPage: string;
    cut: string;
    copy: string;
    paste: string;
    withoutFormatting: string;
    showRuler: string;
    printLayout: string;
    fullScreen: string;
    text: string;
    superscript: string;
    subscript: string;
    upperCase: string;
    lowerCase: string;
    titleCase: string;
    alignAndIndent: string;
    left: string;
    center: string;
    right: string;
    justify: string;
    increaseIndent: string;
    decreaseIndent: string;
    script: string;
    scriptScene: string;
    scriptAction: string;
    scriptCharacter: string;
    scriptDialogue: string;
    scriptParenthetical: string;
    scriptTransition: string;
    scriptNone: string;
    scriptHint: string;
    scriptHintNote: string;
    bulletsAndNumbering: string;
    lineAndSpacing: string;
    spacingSingle: string;
    spacingDouble: string;
    addSpaceBefore: string;
    removeSpaceBefore: string;
    customSpacingMenu: string;
    keepWithNext: string;
    keepLinesTogether: string;
    preventSingleLines: string;
    addPageBreakBefore: string;
    insertRowAbove: string;
    insertRowBelow: string;
    insertColumnLeft: string;
    insertColumnRight: string;
    toggleHeaderRow: string;
    deleteRow: string;
    deleteColumn: string;
    deleteTable: string;
    wordCount: string;
    proofreadSettings: string;
    infoSourceEntries: string;
    infoTags: string;
    infoLinks: string;
    tags: string;
    links: string;
    textStyle: string;
    screenplayElementHint: string;
    notScript: string;
    font: string;
    defaultColor: string;
    noHighlight: string;
    textOutline: string;
    themeDefault: string;
    removeOutline: string;
    insertImageOrPaste: string;
    customColor: string;
    custom: string;
    pageSingle: string;
    pageRange: string;
    pages: string;
    words: string;
    characters: string;
    charactersNoSpaces: string;
    displayWordCountWhileTyping: string;
    wordsLabel: string;
    proofreadIntro: string;
    proofSpelling: string;
    proofSpellingDesc: string;
    proofGrammar: string;
    proofGrammarDesc: string;
    proofActiveVoice: string;
    proofActiveVoiceDesc: string;
    proofConciseness: string;
    proofConcisenessDesc: string;
    proofSentenceSplit: string;
    proofSentenceSplitDesc: string;
    noteLabel: string;
    proofreadNote: string;
    spellAddToDictionary: string;
    spellIgnore: string;
    spellNoSuggestions: string;
    customSpacing: string;
    lineSpacing: string;
    paragraphSpacingPts: string;
    spacingBefore: string;
    spacingAfter: string;
    apply: string;
    placeholder: string;
    clipboardBlockedCut: string;
    clipboardBlockedCopy: string;
    clipboardBlockedPaste: string;
    linkUrlPrompt: string;
    imagePasteFailed: string;
    imageUploadFailed: string;
    exportFailed: string;
  };
  // Organize Document Modal
  orgDoc: {
    title: string;
    documentName: string;
    namePlaceholder: string;
    saving: string;
    instructions: string;
    entries: string;
    preview: string;
    stats: string;
    totalEntries: string;
    totalTags: string;
    totalLinks: string;
    combinedTags: string;
    combinedLinks: string;
    contentPreview: string;
    cancel: string;
    finalize: string;
  };
  // Common
  // Sharing an item with other people, and living inside someone else's
  sharing: {
    share: string;
    shareTitle: string;
    peopleWithAccess: string;
    onlyYou: string;
    inviteByEmail: string;
    emailPlaceholder: string;
    invite: string;
    inviting: string;
    canEdit: string;
    canView: string;
    pending: string;
    pendingExplain: string;
    emailedLink: string;
    sendThemLink: string;
    copyLink: string;
    copied: string;
    remove: string;
    removeConfirm: string;
    loadFailed: string;
    close: string;
    sharedBy: string;
    youCanEdit: string;
    youCanView: string;
    lastEditedBy: string;
    viewOnly: string;
    othersNote: string;
    sharedWithMeTitle: string;
    nothingShared: string;
    kindNote: string;
    kindDocument: string;
    kindStoryboard: string;
    updated: string;
    leave: string;
    leaveConfirm: string;
    invitedYouEdit: string;
    invitedYouView: string;
    inviteSignIn: string;
    inviteCreateAccount: string;
    inviteAccept: string;
    inviteAccepting: string;
    inviteSignedOutHint: string;
    inviteUsed: string;
    inviteInvalid: string;
    inviteFailed: string;
    conflictTitle: string;
    conflictBody: string;
    conflictLoadTheirs: string;
    conflictKeepMine: string;
    conflictWarning: string;
  };
  // The document explorer's toolbar and folder rows
  explorer: {
    newSubfolder: string;
    newDocumentInFolder: string;
    name: string;
    modified: string;
    created: string;
    grouping: string;
    foldersFirst: string;
    filesFirst: string;
    mixed: string;
  };
  // The tag field on the note composer
  tagInput: {
    label: string;
    required: string;
    editBackgroundColor: string;
    editTextColor: string;
    removeTag: string;
    addAnother: string;
    addTags: string;
    recentClickToAdd: string;
  };
  // Source and external links, on the note composer and the note form
  links: {
    sourceUrls: string;
    externalLinks: string;
    enterUrl: string;
    typeReference: string;
    typeVideo: string;
    typeMeme: string;
    add: string;
    removeLink: string;
    collapseSection: string;
  };
  // The in-app About page, including its privacy policy. Bodies use **bold** and *italic* markers, rendered by About.tsx.
  about: {
    tagline: string;
    whatIsTitle: string;
    whatIsP1: string;
    whatIsP2: string;
    whoForTitle: string;
    whoForIntro: string;
    whoFor1: string;
    whoFor2: string;
    whoFor3: string;
    whoFor4: string;
    whoFor5: string;
    whoFor6: string;
    whoFor7: string;
    whoFor8: string;
    whoFor9: string;
    whoFor10: string;
    whoFor11: string;
    whoFor12: string;
    howTitle: string;
    howIntro: string;
    featBrainTitle: string;
    featBrainBody: string;
    featTagsTitle: string;
    featTagsBody: string;
    featWebTitle: string;
    featWebBody: string;
    featRecallTitle: string;
    featRecallBody: string;
    featVideoTitle: string;
    featVideoBody: string;
    featVoiceTitle: string;
    featVoiceBody: string;
    featDocsTitle: string;
    featDocsBody: string;
    featSourcesTitle: string;
    featSourcesBody: string;
    featImportTitle: string;
    featImportBody: string;
    featExtensionTitle: string;
    featExtensionBody: string;
    bottomTitle: string;
    bottomP1: string;
    bottomP2: string;
    bottomCta: string;
    privacyTitle: string;
    privacyUpdated: string;
    infoCollectTitle: string;
    infoCollectIntro: string;
    infoCollect1: string;
    infoCollect2: string;
    infoCollect3: string;
    infoCollect4: string;
    infoCollect5: string;
    infoCollect6: string;
    infoCollect7: string;
    howUseTitle: string;
    howUseIntro: string;
    howUse1: string;
    howUse2: string;
    howUse3: string;
    howUse4: string;
    howUse5: string;
    storageTitle: string;
    storageBody: string;
    thirdPartyTitle: string;
    thirdPartyIntro: string;
    thirdParty1: string;
    thirdParty2: string;
    thirdParty3: string;
    thirdPartyOutro: string;
    sharingTitle: string;
    sharingBody: string;
    rightsTitle: string;
    rightsIntro: string;
    rights1: string;
    rights2: string;
    rights3: string;
    rights4: string;
    retentionTitle: string;
    retentionBody: string;
    cookiesTitle: string;
    cookiesBody: string;
    changesTitle: string;
    changesBody: string;
    contactTitle: string;
    contactBody: string;
    gdprTitle: string;
    gdprBody: string;
    ccpaTitle: string;
    ccpaBody: string;
    copyright: string;
    version: string;
  };
  // The "here's what you'd get" teaser a free user sees at a premium feature
  premium: {
    ocrTitle: string;
    ocrTagline: string;
    ocrBullet1: string;
    ocrBullet2: string;
    ocrBullet3: string;
    aiTaggingTitle: string;
    aiTaggingTagline: string;
    aiTaggingBullet1: string;
    aiTaggingBullet2: string;
    aiTaggingBullet3: string;
    discoveryTitle: string;
    discoveryTagline: string;
    discoveryBullet1: string;
    discoveryBullet2: string;
    discoveryBullet3: string;
    videoSummaryTitle: string;
    videoSummaryTagline: string;
    videoSummaryBullet1: string;
    videoSummaryBullet2: string;
    videoSummaryBullet3: string;
    importTitle: string;
    importTagline: string;
    importBullet1: string;
    importBullet2: string;
    importBullet3: string;
    collaborationTitle: string;
    collaborationTagline: string;
    collaborationBullet1: string;
    collaborationBullet2: string;
    collaborationBullet3: string;
    premiumFeature: string;
    seePlans: string;
    maybeLater: string;
  };
  // The Discover view, its free-plan teaser and the note graph
  discover: {
    teaserTitle: string;
    teaserBody: string;
    teaserLock: string;
    teaserCta: string;
    title: string;
    hint: string;
    noTaggedNotes: string;
    loadMore: string;
    noText: string;
    placeholderTitle: string;
    placeholderBody: string;
    placeholderSub: string;
    graphClickToBranch: string;
    graphEmpty: string;
    graphHint: string;
  };
  // The storyboard list and the storyboard editor
  storyboards: {
    title: string;
    newStoryboard: string;
    loading: string;
    noneYet: string;
    createFirst: string;
    createStoryboard: string;
    createTitle: string;
    name: string;
    namePlaceholder: string;
    descriptionOptional: string;
    descriptionPlaceholder: string;
    create: string;
    entryOne: string;
    entryMany: string;
    updated: string;
    loadFailed: string;
    nameRequired: string;
    createFailed: string;
    deleteConfirm: string;
    deleteFailed: string;
    loadingOne: string;
    loadOneFailed: string;
    docCreateFailed: string;
    noteSaveFailed: string;
    addEntryFailed: string;
    removeEntryFailed: string;
    orderSaveFailed: string;
    removeEntryConfirm: string;
    createDocument: string;
    createDocumentTooltip: string;
    addEntries: string;
    expand: string;
    collapse: string;
    searchPlaceholder: string;
    newNoteTagged: string;
    searching: string;
    noContent: string;
    addToStoryboard: string;
    searchPrompt: string;
    searchPromptHint: string;
    boardHeading: string;
    empty: string;
    emptyHint: string;
    cardNotePlaceholder: string;
    cardNoteTitle: string;
    addCardNote: string;
    docDialogTitle: string;
    docDialogHint: string;
    docNamePlaceholder: string;
    creating: string;
    createAndOpen: string;
  };
  // Subscription, usage limits and checkout
  billing: {
    planFree: string;
    planMonthly: string;
    planYearly: string;
    planComp: string;
    premiumBadge: string;
    unlimited: string;
    loadFailed: string;
    loading: string;
    cancelConfirm: string;
    cancelFailed: string;
    canceling: string;
    cancelSubscription: string;
    pastDueWarning: string;
    canceledWarning: string;
    switchToYearlyConfirm: string;
    switchToMonthlyConfirm: string;
    switchToYearly: string;
    switchToMonthly: string;
    usageNotes: string;
    usageDocuments: string;
    usageStoryboards: string;
    usageStorage: string;
    upgradeMonthly: string;
    upgradeYearly: string;
    checkoutPlanMonthly: string;
    checkoutPlanYearly: string;
    upgradeHeading: string;
    priceMonthly: string;
    priceYearly: string;
    preparingCheckout: string;
    confirmingSubscription: string;
    trialStarted: string;
    done: string;
    paymentFormLoadFailed: string;
    checkoutStartFailed: string;
    paymentFailed: string;
    paymentNotConfirmed: string;
    confirmationFailed: string;
  };
  // The two import dialogs opened from the document editor's File > Import menu — bringing outside files and Google Docs in as documents of their own. Kept out of `editor` because they are about getting documents into Yarnboard, not about editing one.
  importDocs: {
    importFailed: string;
    localTitle: string;
    dropzoneTitle: string;
    supportedFormats: string;
    selectedFiles: string;
    importing: string;
    importFileOne: string;
    importFileMany: string;
    pickerLoadFailed: string;
    pickerTitle: string;
    authCancelled: string;
    googleTitle: string;
    loadingPicker: string;
    unconfiguredTitle: string;
    unconfiguredDesc: string;
    googleHeading: string;
    googleDesc: string;
    importingEllipsis: string;
    chooseFromDrive: string;
    complete: string;
    importedOne: string;
    importedMany: string;
    imagesSkippedOne: string;
    imagesSkippedMany: string;
    failedOne: string;
    failedMany: string;
    done: string;
  };
  common: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    close: string;
    loading: string;
    error: string;
    success: string;
    confirm: string;
    back: string;
  };
}

export const translations: Record<string, Translations> = {
  en: {
    nav: {
      home: 'Home',
      documents: 'Documents',
      visualize: 'Visualize',
      discover: 'Discover',
      settings: 'Settings',
      upgrade: 'Upgrade',
      addNote: 'Add Note',
      addDocument: 'Add Document',
      tagManagement: 'Tags',
      importDocuments: 'Import Documents',
      sharedWithMe: 'Shared with me',
      notes: 'Notes',
      storyboards: 'Storyboards',
      storyboardsTab: 'Storyboards',
      shared: 'Shared',
      about: 'About',
      feedback: 'Feedback',
      addFolder: 'Add Folder',
      addStoryboard: 'Add Storyboard',
    },
    home: {
      searchPlaceholder: 'Search entries and documents...',
      loading: 'Loading...',
      noEntries: 'No entries found',
      noDocuments: 'No documents found',
      createFirstNote: 'Create your first note to get started',
      createFirstDocument: 'Create your first document to get started',
      selectEntries: 'Select entries to create a document',
      cancel: 'Cancel',
      next: 'Next',
      selected: 'selected',
      createDocumentFromNotes: 'Create Document from Notes',
      newFolder: 'New Folder',
      untitledNote: 'Untitled note',
      subfolderNamePrompt: 'Subfolder name:',
      folderCreateFailed: 'Failed to create folder — a folder with that name may already exist here.',
      searchDocumentsPlaceholder: 'Search documents...',
      searchNotesPlaceholder: 'Search notes...',
      includeRelated: 'Include related notes',
      includeRelatedHint: 'Also show notes connected by shared tags, after the direct matches',
      createNewFolder: 'Create new folder',
      noNotes: 'No notes found',
      tryDifferentSearch: 'Try a different search query',
      clickAddNote: 'Click "Add Note" to get started',
      relatedNotes: 'Related notes — connected by shared tags',
      closeMatch: 'Close match',
      matchedTag: 'Matched tag',
      connectedBy: 'Connected by:',
      inPdf: 'In PDF',
      loadingMoreNotes: 'Loading more notes...',
      importLocal: 'Local Computer',
      importLocalDesc: 'Upload files from your device',
      importGoogleDocs: 'Google Docs',
      importGoogleDocsDesc: 'Import from Google Drive',
      suggestionsTags: 'Tags',
      suggestionsRecent: 'Recent',
      removeFromHistory: 'Remove {query} from history',
      clearSearch: 'Clear search',
    },
    note: {
      edit: 'Edit',
      delete: 'Delete',
      viewImage: 'View Image',
      tags: 'Tags',
      links: 'Links',
      createdOn: 'Created',
      clickToCopy: 'Click to copy.',
      clickToOpenPdf: 'Click to open PDF.',
      openPdf: 'Open PDF',
      downloadPdf: 'Download PDF',
      seeThreads: 'See the threads',
      viewMore: 'View more',
      close: 'Close',
      confirmDelete: 'Are you sure you want to delete this note?',
      imageCopied: 'Image copied to clipboard.',
      textCopied: 'Text copied to clipboard.',
      mediaAlt: 'Note media',
      loadingImage: 'Loading image...',
      noImageFile: 'No image file',
      noImageUrl: 'No image URL',
      fullSizeAlt: 'Full size',
    },
    document: {
      open: 'Open',
      rename: 'Rename',
      delete: 'Delete',
      sources: 'sources',
      lastModified: 'Last modified',
      createdOn: 'Created',
      deleteDocument: 'Delete document',
      confirmDeleteDocument: 'Delete "{name}"? This can\'t be undone.',
      selectedCount: '{count} selected',
      clearSelection: 'Clear',
      dragSelectionHint: 'Drag any of them into a folder to move them all',
      deleteSelected: 'Delete documents',
      confirmDeleteSelected: 'Delete {count} documents? This can\'t be undone.',
    },
    folder: {
      createFolder: 'Create New Folder',
      newFolder: 'New Folder',
      folderName: 'Folder Name',
      enterFolderName: 'Enter folder name...',
      create: 'Create Folder',
      creating: 'Creating...',
      cancel: 'Cancel',
      delete: 'Delete',
      deleteFolder: 'Delete Folder',
      cannotDeleteWithDocs: 'Cannot delete folder because it contains documents. Please move or delete the documents first.',
      confirmDelete: 'Are you sure you want to delete the folder',
      confirmDeleteMessage: 'This action cannot be undone.',
      noDocuments: 'No documents in this folder',
      dragDropHint: 'Drag and drop documents here to organize them',
      expandFolder: 'Expand folder',
      collapseFolder: 'Collapse folder',
      confirmDeleteFolder: 'Delete the folder "{name}"? This can\'t be undone.',
      folderHolds: '"{name}" holds {contents}.',
      documentsMoveToTop: 'The documents move to the top level.',
      subfoldersRemoved: 'Its subfolders are removed and their documents move to the top level.',
      alsoDeleteDocuments: 'Delete the {count} inside as well',
      alsoDeleteDocumentsHint: 'Permanent — the documents are gone for good.',
      typeToConfirm: 'Type {word} to confirm',
      deleting: 'Deleting...',
      documentOne: 'document',
      documentMany: 'documents',
      subfolderOne: 'subfolder',
      subfolderMany: 'subfolders',
    },
    tagManagement: {
      title: 'Tag Management',
      searchTags: 'Search tags...',
      sortBy: 'Sort by',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A',
      mostUsed: 'Most Used',
      leastUsed: 'Least Used',
      color: 'Color',
      textColor: 'Text Color',
      selected: 'selected',
      mergeSelected: 'Merge Selected',
      deleteSelected: 'Delete Selected',
      totalTags: 'Total Tags',
      showing: 'Showing',
      totalEntries: 'Total Entries',
      entries: 'entries',
      edit: 'Edit',
      editTag: 'Edit Tag',
      name: 'Name',
      tagName: 'Tag name',
      saveChanges: 'Save Changes',
      cancel: 'Cancel',
      mergeTags: 'Merge Tags',
      mergeInto: 'Merge into',
      confirmMerge: 'Confirm Merge',
      confirmMergeMessage: 'This will merge the following tags',
      cannotUndo: 'This action cannot be undone. Continue?',
      confirmDelete: 'Confirm Delete',
      confirmDeleteMessage: 'Delete {count} tags?',
      willRemoveTags: 'This will remove these tags from {count} entries.',
      deleteTags: 'Delete Tags',
      selectAtLeast2: 'Please select at least 2 tags to merge',
      selectTagsToDelete: 'Please select tags to delete',
      loadingTags: 'Loading tags...',
      enterTagName: 'Enter tag name',
      preview: 'preview',
      failedToUpdate: 'Failed to update tag',
      failedToMerge: 'Failed to merge tags',
      failedToDelete: 'Failed to delete tags',
    },
    settings: {
      title: 'Settings',
      account: 'Account',
      appearance: 'Appearance',
      language: 'Language',
      help: 'Help',
      about: 'About',
      accountInfo: 'Account Information',
      name: 'Name',
      displayName: 'Display Name',
      enterName: 'Enter your name',
      updateName: 'Update Name',
      email: 'Email',
      emailLabel: 'Email',
      currentEmail: 'Current Email',
      changeEmail: 'Change Email',
      newEmail: 'New Email',
      enterNewEmail: 'Enter new email',
      updateEmail: 'Update Email',
      updating: 'Updating...',
      password: 'Password',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      enterCurrentPassword: 'Enter current password',
      newPassword: 'New Password',
      enterNewPassword: 'Enter new password',
      confirmPassword: 'Confirm Password',
      confirmNewPassword: 'Confirm new password',
      updatePassword: 'Update Password',
      subscription: 'Subscription',
      subscriptionPlaceholder: 'Subscription management coming soon.',
      logout: 'Log Out',
      logoutButton: 'Log Out',
      selectTheme: 'Theme',
      darkTheme: 'Dark Mode',
      lightTheme: 'Light Mode',
      selectLanguage: 'Select your preferred language',
      english: 'English',
      spanish: 'Español (Latinoamérica)',
      portuguese: 'Português (Brasil)',
      feedbackTitle: 'Send feedback',
      feedbackDesc: 'Found a bug or have an idea? Send it straight to the developer.',
      feedbackTypeBug: 'Bug',
      feedbackTypeIdea: 'Idea / request',
      feedbackTypeOther: 'Other',
      feedbackPlaceholder: 'Describe the bug or idea…',
      feedbackSend: 'Send feedback',
      feedbackSending: 'Sending…',
      feedbackThanks: 'Thanks! Your feedback was sent.',
      feedbackError: 'Could not send. Please try again.',
      helpSupport: 'Help & Support',
      searchHelp: 'Search help articles...',
      searchButton: 'Search',
      helpPlaceholder: 'Help articles and video tutorials coming soon.',
      aboutApp: 'About Yarnboard',
      version: 'Version 1.0.0',
      aboutDescription: 'Yarnboard is a powerful note-taking and document management application.',
      aboutPlaceholder: 'More information coming soon.',
      dangerZone: 'Danger Zone',
      deactivateAccount: 'Deactivate Account',
      deactivateWarning: 'Deactivating your account will temporarily disable it. You can reactivate it by logging in again.',
      deactivate: 'Deactivate Account',
      deleteAccount: 'Delete Account',
      deleteWarning: 'This action cannot be undone. All your data will be permanently deleted.',
      deleteAccountButton: 'Delete Account Permanently',
      emailUpdated: 'Email updated successfully',
      nameUpdated: 'Name updated successfully',
      passwordUpdated: 'Password updated successfully',
      passwordChanged: 'Password changed successfully',
      themeUpdated: 'Theme updated successfully',
      languageUpdated: 'Language updated successfully',
      passwordMismatch: 'New passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters long',
      updateFailed: 'Failed to update',
      failedToUpdate: 'Failed to update',
      failedToLoad: 'Failed to load settings',
      failedToChange: 'Failed to change password',
      loading: 'Loading...',
      currentPasswordPrompt: 'Enter your current password to change your email:',
      confirmDeactivate: 'Are you sure you want to deactivate your account? You can reactivate it by logging in again.',
      failedToDeactivate: 'Failed to deactivate account',
      deletePrompt: 'Type "DELETE" to permanently delete your account and all data:',
      failedToDeleteAccount: 'Failed to delete account',
      moreThemesSoon: 'Additional color themes coming soon.',
    },
    addNote: {
      title: 'Add Note',
      cancel: 'Cancel',
      remember: 'Remember',
      content: 'Content',
      contentPlaceholder: 'What would you like to remember?',
      tags: 'Tags',
      tagsPlaceholder: 'Add tags...',
      links: 'Links',
      addLink: 'Add Link',
      linkUrl: 'URL',
      linkTitle: 'Title (optional)',
      image: 'Image',
      uploadImage: 'Upload Image',
      pasteImage: 'or paste from clipboard',
      error: 'Error',
      aiSuggest: 'Auto tagging',
      generatingTags: 'Generating tags...',
      tagSuggestionFailed: 'Failed to generate tags',
      noTextInImage: 'No text found in image',
      aiOffNeedsTag: 'Add at least one tag, or switch auto tagging back on.',
      summarizeVideo: 'Summarize the YouTube video',
      summarizeVideoDisabled: 'Add a YouTube link to summarize it',
      summarizingVideo: 'Watching the video...',
      videoSummaryHeading: 'Video summary',
      videoSummaryFailed: 'Failed to summarize the video',
      saving: 'Saving...',
      pasteImageFailed: 'Failed to process pasted image',
      loadNoteFailed: 'Failed to load note data',
      unsupportedFile: 'Unsupported file type',
      fileFailed: 'Failed to process file',
      ocrFailed: 'Failed to extract text from image',
      addContentFirst: 'Please add some content before generating tags',
      contentRequired: 'Content or media is required.',
      tagRequiredFree: 'Add at least one tag to save your note.',
      tagRequired: 'At least one tag is required.',
      saveFailed: 'Failed to save note',
      addAttachment: 'Add image, video or PDF',
      extractText: 'Extract text from image',
      addSourceUrls: 'Add source URLs',
      aiInfo: 'AI results may vary. For best results, add a few tags of your own first to help guide the suggestions.',
      codeBlockHint: 'Code block (or type ```)',
      equation: 'Equation',
      equationHint: 'Equation (or type $x^2$)',
      attachedAlt: 'Attached',
      removeImage: 'Remove image',
      removePdf: 'Remove PDF',
      codeLanguage: 'Code language',
      latexPrompt: 'LaTeX',
      latexInsertPrompt: 'LaTeX (e.g. E = mc^2)',
    },
    editNote: {
      title: 'Edit Note',
      update: 'Update',
    },
    addDocument: {
      title: 'Create New Document',
      documentName: 'Document Name',
      documentNamePlaceholder: 'Document Name',
      create: 'Create',
      creating: 'Creating...',
      cancel: 'Cancel',
      nameRequired: 'Document name is required.',
      failedToCreate: 'Failed to create document',
    },
    editor: {
      untitled: 'Untitled Document',
      saving: 'Saving...',
      saved: 'Saved',
      unsaved: 'Unsaved changes',
      share: 'Share',
      info: 'Document Info',
      back: 'Back',
      convertToEntry: 'Convert document to single entry',
      convertToFootnote: 'Convert selection to footnote',
      file: 'File',
      edit: 'Edit',
      insert: 'Insert',
      format: 'Format',
      view: 'View',
      tools: 'Tools',
      help: 'Help',
      print: 'Print',
      undo: 'Undo',
      redo: 'Redo',
      selectAll: 'Select All',
      bold: 'Bold',
      italic: 'Italic',
      underline: 'Underline',
      strikethrough: 'Strikethrough',
      textColor: 'Text color',
      highlightColor: 'Highlight color',
      fontSize: 'Font size',
      decreaseFontSize: 'Decrease font size',
      increaseFontSize: 'Increase font size',
      heading: 'Heading',
      heading1: 'Heading 1',
      heading2: 'Heading 2',
      heading3: 'Heading 3',
      heading4: 'Heading 4',
      heading5: 'Heading 5',
      heading6: 'Heading 6',
      paragraph: 'Paragraph',
      bulletList: 'Bullet list',
      numberedList: 'Numbered list',
      checkList: 'Check list',
      alignLeft: 'Align left',
      alignCenter: 'Align center',
      alignRight: 'Align right',
      alignJustify: 'Justify',
      indent: 'Indent',
      outdent: 'Outdent',
      link: 'Link',
      image: 'Image',
      table: 'Table',
      horizontalRule: 'Horizontal rule',
      blockquote: 'Blockquote',
      codeBlock: 'Code block',
      clearFormatting: 'Clear formatting',
      builtFrom: 'Built from these notes',
      sourcesChanged: 'A note this was written from has changed since.',
      sourceChanged: 'changed',
      writerNote: 'Note to self',
      writerNoteHint: 'Note to self — kept out of anything you export',
      versionHistory: 'Version history',
      versionHistoryIntro: 'Yarnboard keeps the last 15 versions of this document as you write, so anything deleted or changed by mistake can be brought back.',
      versionsLoading: 'Loading versions…',
      noVersionsYet: 'No earlier versions yet. They are saved as you write.',
      versionWords: 'words',
      restoreVersion: 'Restore',
      restoreConfirm: 'Replace the document with this version? What is there now will be kept in the history, so you can undo this.',
      restoreFailed: 'Could not restore this version. Please try again.',
      loadingDocument: 'Loading document...',
      loadFailed: 'Failed to load document',
      saveFailed: 'Failed to save document',
      backToEntryForm: 'Back to Entry Form',
      importMenu: 'Import',
      importLocalComputer: 'Local Computer',
      download: 'Download',
      downloadPdf: 'PDF (.pdf)',
      downloadWord: 'Word (.docx)',
      downloadRichText: 'Rich Text (.rtf)',
      downloadWebPage: 'Web Page (.html)',
      cut: 'Cut',
      copy: 'Copy',
      paste: 'Paste',
      withoutFormatting: '(without formatting)',
      showRuler: 'Show Ruler',
      printLayout: 'Print layout',
      fullScreen: 'Full screen',
      text: 'Text',
      superscript: 'Superscript',
      subscript: 'Subscript',
      upperCase: 'UPPERCASE',
      lowerCase: 'lowercase',
      titleCase: 'Title Case',
      alignAndIndent: 'Align & indent',
      left: 'Left',
      center: 'Center',
      right: 'Right',
      justify: 'Justify',
      increaseIndent: 'Increase indent',
      decreaseIndent: 'Decrease indent',
      script: 'Script',
      scriptScene: 'Scene heading',
      scriptAction: 'Action',
      scriptCharacter: 'Character',
      scriptDialogue: 'Dialogue',
      scriptParenthetical: 'Parenthetical',
      scriptTransition: 'Transition',
      scriptNone: 'Normal text',
      scriptHint: 'Tab cycles elements · Enter flows',
      scriptHintNote: '(character→dialogue…)',
      bulletsAndNumbering: 'Bullets & numbering',
      lineAndSpacing: 'Line & spacing',
      spacingSingle: 'Single',
      spacingDouble: 'Double',
      addSpaceBefore: 'Add space before paragraph',
      removeSpaceBefore: 'Remove space before paragraph',
      customSpacingMenu: 'Custom spacing...',
      keepWithNext: 'Keep with next',
      keepLinesTogether: 'Keep lines together',
      preventSingleLines: 'Prevent single lines',
      addPageBreakBefore: 'Add page break before',
      insertRowAbove: 'Insert row above',
      insertRowBelow: 'Insert row below',
      insertColumnLeft: 'Insert column left',
      insertColumnRight: 'Insert column right',
      toggleHeaderRow: 'Toggle header row',
      deleteRow: 'Delete row',
      deleteColumn: 'Delete column',
      deleteTable: 'Delete table',
      wordCount: 'Word count',
      proofreadSettings: 'Proofread Settings',
      infoSourceEntries: 'Source Entries:',
      infoTags: 'Tags:',
      infoLinks: 'Links:',
      tags: 'Tags',
      links: 'Links',
      textStyle: 'Text style',
      screenplayElementHint: 'Screenplay element — Tab cycles, Enter flows to the next',
      notScript: 'Not script',
      font: 'Font',
      defaultColor: 'Default color',
      noHighlight: 'No highlight',
      textOutline: 'Text outline',
      themeDefault: 'Theme default',
      removeOutline: 'Remove outline',
      insertImageOrPaste: 'Insert image (or paste)',
      customColor: 'Custom color',
      custom: 'Custom',
      pageSingle: 'Page 1',
      pageRange: 'Pages 1–{count}',
      pages: 'Pages',
      words: 'Words',
      characters: 'Characters',
      charactersNoSpaces: 'Characters excluding spaces',
      displayWordCountWhileTyping: 'Display word count while typing',
      wordsLabel: 'Words:',
      proofreadIntro: 'Choose which proofreading features to enable while you type:',
      proofSpelling: 'Spelling',
      proofSpellingDesc: 'Underlines misspelled words in red',
      proofGrammar: 'Grammar',
      proofGrammarDesc: 'Underlines grammar issues in blue',
      proofActiveVoice: 'Active Voice',
      proofActiveVoiceDesc: 'Detects and suggests active voice alternatives',
      proofConciseness: 'Conciseness',
      proofConcisenessDesc: 'Suggests shorter alternatives and removes wordiness',
      proofSentenceSplit: 'Sentence Split',
      proofSentenceSplitDesc: 'Identifies overly complex sentences',
      noteLabel: 'Note:',
      proofreadNote: 'Spelling is checked in English, Spanish and Brazilian Portuguese, picked paragraph by paragraph from the words you write. Right-click an underlined word for suggestions or to add it to your dictionary.',
      spellAddToDictionary: 'Add to dictionary',
      spellIgnore: 'Ignore',
      spellNoSuggestions: 'No suggestions',
      customSpacing: 'Custom Spacing',
      lineSpacing: 'Line spacing',
      paragraphSpacingPts: 'Paragraph spacing (pts)',
      spacingBefore: 'Before',
      spacingAfter: 'After',
      apply: 'Apply',
      placeholder: 'Start typing your document...',
      clipboardBlockedCut: 'Your browser blocked the cut — use ⌘X / Ctrl+X instead.',
      clipboardBlockedCopy: 'Your browser blocked the copy — use ⌘C / Ctrl+C instead.',
      clipboardBlockedPaste: 'Your browser blocked the paste — use ⌘V / Ctrl+V instead.',
      linkUrlPrompt: 'Link URL (leave empty to remove the link):',
      imagePasteFailed: 'Failed to upload pasted image. Please try again.',
      imageUploadFailed: 'Failed to upload image. Please try again.',
      exportFailed: 'Export failed. Please try again.',
    },
    orgDoc: {
      title: 'Organize Document',
      documentName: 'Document Name',
      namePlaceholder: 'Enter document name...',
      saving: 'Saving...',
      instructions: 'Drag entries to reorder them',
      entries: 'Entries',
      preview: 'Preview',
      stats: 'Document Statistics',
      totalEntries: 'Total Entries',
      totalTags: 'Total Tags',
      totalLinks: 'Total Links',
      combinedTags: 'Combined Tags',
      combinedLinks: 'Combined Links',
      contentPreview: 'Content Preview',
      cancel: 'Cancel',
      finalize: 'Finalize Document',
    },
    sharing: {
      share: 'Share',
      shareTitle: 'Share “{name}”',
      peopleWithAccess: 'People with access',
      onlyYou: 'Only you have access so far.',
      inviteByEmail: 'Invite by email',
      emailPlaceholder: 'name@example.com',
      invite: 'Invite',
      inviting: 'Inviting…',
      canEdit: 'Can edit',
      canView: 'Can view',
      pending: 'Pending',
      pendingExplain: 'Pending means they have not opened the invite yet. Access starts when they open the link and sign in.',
      emailedLink: 'We emailed them the link.',
      sendThemLink: 'Send them this link.',
      copyLink: 'Copy link',
      copied: 'Copied',
      remove: 'Remove',
      removeConfirm: 'Stop sharing with {name}?',
      loadFailed: 'Could not load who has access.',
      close: 'Close',
      sharedBy: 'Shared by {name}',
      youCanEdit: 'You can edit',
      youCanView: 'You can view',
      lastEditedBy: 'Last edited by {name}',
      viewOnly: 'View only',
      othersNote: '{name}’s note',
      sharedWithMeTitle: 'Shared with me',
      nothingShared: 'Nothing has been shared with you yet.',
      kindNote: 'Notes',
      kindDocument: 'Documents',
      kindStoryboard: 'Storyboards',
      updated: 'Updated {date}',
      leave: 'Leave',
      leaveConfirm: 'Leave “{name}”? You lose access until they share it with you again.',
      invitedYouEdit: '{name} invited you to edit “{title}”',
      invitedYouView: '{name} invited you to view “{title}”',
      inviteSignIn: 'Sign in',
      inviteCreateAccount: 'Create account',
      inviteAccept: 'Accept invitation',
      inviteAccepting: 'Opening…',
      inviteSignedOutHint: 'Sign in with {email}, or create an account with it, and you will come straight back here.',
      inviteUsed: 'This invitation has already been used by someone else.',
      inviteInvalid: 'This invitation is no longer valid.',
      inviteFailed: 'Could not accept this invitation.',
      conflictTitle: 'Someone else saved first',
      conflictBody: '{name} saved a newer version while you were writing.',
      conflictLoadTheirs: 'Load their version',
      conflictKeepMine: 'Keep mine',
      conflictWarning: 'Loading their version replaces what is on your screen. Keeping yours saves over theirs.',
    },
    // The document explorer's toolbar and folder rows
    explorer: {
      newSubfolder: 'New subfolder',
      newDocumentInFolder: 'New document in this folder',
      name: 'Name',
      modified: 'Modified',
      created: 'Created',
      grouping: 'Grouping',
      foldersFirst: 'Folders first',
      filesFirst: 'Files first',
      mixed: 'Mixed',
    },
    // The tag field on the note composer
    tagInput: {
      label: 'Tags',
      required: '· required',
      editBackgroundColor: 'Edit background color',
      editTextColor: 'Edit text color',
      removeTag: 'Remove tag',
      addAnother: 'Add another…',
      addTags: 'Add tags… press Enter',
      recentClickToAdd: 'Recent · click to add',
    },
    // Source and external links, on the note composer and the note form
    links: {
      sourceUrls: 'Source URLs',
      externalLinks: 'External Links',
      enterUrl: 'Enter URL...',
      typeReference: 'Reference',
      typeVideo: 'Video',
      typeMeme: 'Meme',
      add: 'Add',
      removeLink: 'Remove link',
      collapseSection: 'Collapse section',
    },
    // The in-app About page, including its privacy policy. Bodies use **bold** and *italic* markers, rendered by About.tsx.
    about: {
      tagline: 'Connect. The. Dots.',
      whatIsTitle: 'What Is This Thing?',
      whatIsP1: 'You know that feeling when you *know* you saw something—a meme, a tweet, that one video—but you can\'t find it? You\'re scrolling through months of chaos trying to piece together that **one perfect argument** you had bookmarked in your brain?',
      whatIsP2: '**Welcome to Yarnboard.** This isn\'t just another note-taking app. This is your *digital conspiracy wall*. Red string and all. Except the red string is actually intelligent, the pushpins are searchable, and nothing ever falls off at 3 AM.',
      whoForTitle: 'Who Is This For?',
      whoForIntro: 'You. Specifically you. Especially if you\'re:',
      whoFor1: '**Investigative journalists** tracking sources across a dozen rabbit holes',
      whoFor2: '**Documentary filmmakers** drowning in footage timestamps and interview quotes',
      whoFor3: '**Authors** who refuse to lose that perfect line you thought of in the shower',
      whoFor4: '**Researchers** managing citations like you\'re defending a dissertation to a particularly hostile committee',
      whoFor5: '**YouTubers** who need to cite sources before someone points out you\'re wrong in the comments',
      whoFor6: '**Educators** building lesson plans from scattered PDFs and bookmarked articles',
      whoFor7: '**Sketch comedians** storing joke premises and that one perfect comeback for later',
      whoFor8: '**Media literacy advocates** tracking misinformation like you\'re hunting Bigfoot (but Bigfoot is real and it\'s disinformation)',
      whoFor9: '**Twitter/Bluesky addicts** who need to instantly recall that thread from three weeks ago',
      whoFor10: '**Lawyers** who *definitely* don\'t want to lose track of evidence',
      whoFor11: '**Coaches** managing training materials and motivational quotes',
      whoFor12: '**Anyone** who\'s ever said "I swear I saw this somewhere..."',
      howTitle: 'The Conspiracy (How It Works)',
      howIntro: 'Imagine a **literal yarnboard**—you know, the kind where detectives pin photos and connect them with red string? Now imagine that board is *infinite*, the string connects itself, and you can search the entire thing by typing three letters.',
      featBrainTitle: '🧠 Your External Brain',
      featBrainBody: 'Every meme. Every screenshot. Every "I should remember this" moment. Store it here. OCR technology reads text from images so you can search for that tweet screenshot you took in 2022 by typing "climate" or "ratio\'d" or whatever the kids are saying now.',
      featTagsTitle: '🏷️ Everything Gets Tagged',
      featTagsBody: 'No tag? No entry. We\'re not letting you become a digital hoarder. Everything needs at least one label. The AI helps you tag stuff automatically because it actually *reads* what you\'re storing. Your future self will thank you when you find that thing in 2 seconds instead of 2 hours.',
      featWebTitle: '🕸️ The Web of Connections',
      featWebBody: 'A visual mind map that draws connections you didn\'t even know existed. That podcast quote? It relates to these three articles, that documentary, and this tweet thread. **The dots connect themselves**. You\'re welcome.',
      featRecallTitle: '🎯 Instant Recall',
      featRecallBody: '"I keep saying this in social media comments..." Just search, tap, copy, paste. Done. That citation you need? That perfect comeback? That video link someone asked for? It\'s right here. Always.',
      featVideoTitle: '📹 Video? Transcribed.',
      featVideoBody: 'Drop in a YouTube link. Yarnboard rips the audio, transcribes it, and makes it searchable. Now when someone asks "What was that thing from that video?" you can actually answer instead of saying "It was... uh... I\'ll find it later" (you won\'t).',
      featVoiceTitle: '🎙️ Voice Notes That Don\'t Disappear',
      featVoiceBody: 'Record voice memos. They get transcribed and stored. Because sometimes you have brilliant ideas while driving and can\'t write them down without causing a traffic incident.',
      featDocsTitle: '📄 Documents From Chaos',
      featDocsBody: 'Gather scattered entries, search your brain dump, and assemble them into actual documents. Split entries when they\'re too broad. Combine them when they\'re too specific. Drag, drop, edit, reorganize. It\'s like LEGO but for information.',
      featSourcesTitle: '🔗 All Your Sources, Organized',
      featSourcesBody: 'Store unlimited URL links with each entry. Citations? References? "Where did I see this?" It\'s all there. Lawyers and researchers: you\'re *especially* going to appreciate this.',
      featImportTitle: '📥 Import Everything',
      featImportBody: 'Got existing documents? PDFs? Google Docs? Throw them in. The AI reads them, tags them, and integrates them into your knowledge web. Your old notes aren\'t useless anymore—they\'re *connected*.',
      featExtensionTitle: '🌐 Coming Soon: Browser Extension',
      featExtensionBody: 'See something interesting? One click. Saved. Tagged. Connected. It\'s like web clipping but for people who actually *use* their web clippings.',
      bottomTitle: 'The Bottom Line',
      bottomP1: 'Yarnboard isn\'t just an app. It\'s a **conspiracy wall for your brain**. Everything you see, read, watch, or think gets stored, organized, connected, and instantly searchable. When you need to find that thing—you *will* find that thing.',
      bottomP2: 'Stop losing your thoughts. Start connecting the dots.',
      bottomCta: '**Welcome to the conspiracy. You\'re the detective now.**',
      privacyTitle: 'Privacy Policy',
      privacyUpdated: 'Last Updated: November 8, 2025',
      infoCollectTitle: 'Information We Collect',
      infoCollectIntro: 'Yarnboard collects and stores the information you explicitly choose to save in the application, including:',
      infoCollect1: 'Text entries, notes, and documents you create',
      infoCollect2: 'Images, screenshots, and media files you upload',
      infoCollect3: 'Audio recordings and their transcriptions',
      infoCollect4: 'URLs and web links you save',
      infoCollect5: 'Tags and organizational labels you create',
      infoCollect6: 'Video links and associated transcriptions',
      infoCollect7: 'Your account information (email, username, password)',
      howUseTitle: 'How We Use Your Information',
      howUseIntro: 'Your data is used solely to provide Yarnboard\'s core functionality:',
      howUse1: 'Storing and organizing your saved content',
      howUse2: 'Enabling search and retrieval of your entries',
      howUse3: 'Creating visual connections between related content',
      howUse4: 'Processing OCR on images and transcription of audio/video',
      howUse5: 'Generating AI-powered tag suggestions',
      storageTitle: 'Data Storage and Security',
      storageBody: 'Your content is stored securely in cloud-based servers with encryption in transit and at rest. We implement industry-standard security measures to protect your data from unauthorized access, alteration, or deletion.',
      thirdPartyTitle: 'Third-Party Services',
      thirdPartyIntro: 'Yarnboard may use third-party services for:',
      thirdParty1: 'Cloud storage and hosting',
      thirdParty2: 'OCR and transcription processing',
      thirdParty3: 'AI-powered content analysis for tagging',
      thirdPartyOutro: 'These services are bound by their own privacy policies and are selected based on their security and privacy standards.',
      sharingTitle: 'Data Sharing',
      sharingBody: '**We do not sell, rent, or share your personal data with third parties for marketing purposes.** Your content remains private and is only accessible to you through your account.',
      rightsTitle: 'Your Rights',
      rightsIntro: 'You have the right to:',
      rights1: 'Access all data associated with your account',
      rights2: 'Export your data in standard formats',
      rights3: 'Delete your data and account at any time',
      rights4: 'Request corrections to your data',
      retentionTitle: 'Data Retention',
      retentionBody: 'Your data is retained as long as your account is active. If you delete your account, all associated data will be permanently deleted within 30 days, except where retention is required by law.',
      cookiesTitle: 'Cookies and Tracking',
      cookiesBody: 'Yarnboard uses essential cookies for authentication and session management. We do not use tracking cookies or third-party advertising cookies.',
      changesTitle: 'Changes to This Policy',
      changesBody: 'We may update this privacy policy from time to time. Significant changes will be communicated through the application or via email. Continued use of Yarnboard after changes constitutes acceptance of the updated policy.',
      contactTitle: 'Contact',
      contactBody: 'For questions about this privacy policy or your data, please contact us through the application\'s support channels.',
      gdprTitle: 'GDPR Compliance',
      gdprBody: 'For users in the European Union, Yarnboard complies with GDPR requirements. You have the right to data portability, the right to be forgotten, and the right to restrict processing of your personal data.',
      ccpaTitle: 'CCPA Compliance',
      ccpaBody: 'For California residents, Yarnboard complies with the California Consumer Privacy Act. You have the right to know what personal information is collected, the right to delete personal information, and the right to opt-out of the sale of personal information (though we do not sell personal information).',
      copyright: '© 2025 Yarnboard. All rights reserved.',
      version: 'Version 1.0 (Codename: Red String)',
    },
    // The "here's what you'd get" teaser a free user sees at a premium feature
    premium: {
      ocrTitle: 'Pull text out of any image',
      ocrTagline: 'Snap a photo of a page, whiteboard, or receipt — Yarnboard reads it into editable text.',
      ocrBullet1: 'Turn screenshots and photos into searchable notes',
      ocrBullet2: 'Great for handwritten pages and printed documents',
      ocrBullet3: 'One tap on the image — the text drops right into your note',
      aiTaggingTitle: 'Let AI tag your notes for you',
      aiTaggingTagline: 'Yarnboard reads your note and suggests the right tags automatically.',
      aiTaggingBullet1: 'Consistent tags without the busywork',
      aiTaggingBullet2: 'Reuses your existing tags when they fit',
      aiTaggingBullet3: 'Keeps your board organized as it grows',
      discoveryTitle: 'Explore your notes as a living graph',
      discoveryTagline: 'Pick any note and watch related notes branch out — surface connections you forgot you made.',
      discoveryBullet1: 'Rare shared tags reveal the most surprising links',
      discoveryBullet2: 'Click any node to branch out further',
      discoveryBullet3: 'Turn a pile of notes into a map of your thinking',
      videoSummaryTitle: 'Summarize a YouTube video into your note',
      videoSummaryTagline: 'Save the link, press the button, and the video\'s points land in your note as text you can search.',
      videoSummaryBullet1: 'Every major point, in the order the video makes them',
      videoSummaryBullet2: 'Names, numbers and dates kept — those are what you search for later',
      videoSummaryBullet3: 'The summary is note text: edit it, tag it, put it in a document',
      importTitle: 'Import your existing documents',
      importTagline: 'Bring in Google Docs and local files (Word, PDF, Markdown) as editable Yarnboard documents.',
      importBullet1: 'Pick Google Docs straight from Drive',
      importBullet2: 'Upload Word, PDF, RTF, Markdown and more',
      importBullet3: 'Everything becomes fully editable in Yarnboard',
      collaborationTitle: 'Write together',
      collaborationTagline: 'Share a storyboard, a document or a single note with the people you work with.',
      collaborationBullet1: 'Invite anyone by email as an editor or a viewer',
      collaborationBullet2: 'Their notes sit on your board beside yours',
      collaborationBullet3: 'Two people in one document never overwrite each other silently',
      premiumFeature: 'Premium feature',
      seePlans: 'See plans & upgrade',
      maybeLater: 'Maybe later',
    },
    // The Discover view, its free-plan teaser and the note graph
    discover: {
      teaserTitle: 'Explore your notes as a living graph',
      teaserBody: 'Pick any note and watch related notes branch out — surfacing connections you forgot you made. Rare shared tags reveal the most surprising links, and every node expands further.',
      teaserLock: '🔒 Premium feature',
      teaserCta: 'See plans & upgrade',
      title: 'Discover Connections',
      hint: 'Pick a note to see what\'s related',
      noTaggedNotes: 'No tagged notes found',
      loadMore: 'Load more',
      noText: '(no text)',
      placeholderTitle: 'Discover hidden connections',
      placeholderBody: 'Select a note from the panel on the left. It becomes the center of a living graph — related notes branch off it, and clicking any node branches out further.',
      placeholderSub: 'Connections come from shared tags. The fewer notes a tag is on, the stronger the connection signal.',
      graphClickToBranch: 'Click to branch out',
      graphEmpty: 'This note\'s tags are unique to it — no other notes share them yet.',
      graphHint: 'Click a node to branch out · drag to rearrange · scroll to zoom',
    },
    // The storyboard list and the storyboard editor
    storyboards: {
      title: 'Storyboards',
      newStoryboard: 'New Storyboard',
      loading: 'Loading storyboards...',
      noneYet: 'No storyboards yet',
      createFirst: 'Create your first storyboard to start organizing your entries visually',
      createStoryboard: 'Create Storyboard',
      createTitle: 'Create New Storyboard',
      name: 'Name',
      namePlaceholder: 'Enter storyboard name...',
      descriptionOptional: 'Description (optional)',
      descriptionPlaceholder: 'Enter description...',
      create: 'Create',
      entryOne: 'entry',
      entryMany: 'entries',
      updated: 'Updated {date}',
      loadFailed: 'Failed to load storyboards',
      nameRequired: 'Storyboard name is required',
      createFailed: 'Failed to create storyboard',
      deleteConfirm: 'Are you sure you want to delete this storyboard?',
      deleteFailed: 'Failed to delete storyboard',
      loadingOne: 'Loading storyboard...',
      loadOneFailed: 'Failed to load storyboard',
      docCreateFailed: 'Failed to create document from storyboard',
      noteSaveFailed: 'Failed to save that note',
      addEntryFailed: 'Failed to add entry',
      removeEntryFailed: 'Failed to remove entry',
      orderSaveFailed: 'Failed to save order',
      removeEntryConfirm: 'Remove this entry from the storyboard?',
      createDocument: 'Create Document',
      createDocumentTooltip: 'Combine this storyboard\'s entries, in order, into a document',
      addEntries: 'Add Entries',
      expand: 'Expand',
      collapse: 'Collapse',
      searchPlaceholder: 'Search entries by content or tags...',
      newNoteTagged: 'New note tagged “{tag}”',
      searching: 'Searching...',
      noContent: '(no content)',
      addToStoryboard: 'Add to storyboard',
      searchPrompt: 'Search for entries to add to your storyboard',
      searchPromptHint: 'Don\'t have any? Add some here, then search for them by the tag you gave them.',
      boardHeading: 'Storyboard',
      empty: 'Your storyboard is empty',
      emptyHint: 'Search and add entries to get started',
      cardNotePlaceholder: 'What is this beat for?',
      cardNoteTitle: 'A note to yourself about this beat. It leads this card in a draft.',
      addCardNote: '+ note',
      docDialogTitle: 'Create document from storyboard',
      docDialogHint: 'All {count} entries will be combined in their storyboard order.',
      docNamePlaceholder: 'Document name',
      creating: 'Creating…',
      createAndOpen: 'Create & open',
    },
    // Subscription, usage limits and checkout
    billing: {
      planFree: 'Free',
      planMonthly: 'Monthly ({price}/mo)',
      planYearly: 'Yearly ({price}/yr)',
      planComp: 'Complimentary (full access)',
      premiumBadge: 'PREMIUM',
      unlimited: 'unlimited',
      loadFailed: 'Could not load subscription info',
      loading: 'Loading…',
      cancelConfirm: 'Cancel your subscription? You keep premium access until the end of the period you already paid for.',
      cancelFailed: 'Cancellation failed',
      canceling: 'Canceling…',
      cancelSubscription: 'Cancel subscription',
      pastDueWarning: 'Last renewal failed — update your payment or access ends {date}.',
      canceledWarning: 'Canceled — premium until {date}.',
      switchToYearlyConfirm: 'Switch to Yearly ({price}/yr — save {saving}%)? Your monthly plan is canceled and the yearly plan starts now.',
      switchToMonthlyConfirm: 'Switch to Monthly ({price}/mo)? Your yearly plan is canceled (access runs through what you already paid) and monthly billing starts now.',
      switchToYearly: 'Switch to Yearly (save {saving}%)',
      switchToMonthly: 'Switch to Monthly',
      usageNotes: 'Notes',
      usageDocuments: 'Documents',
      usageStoryboards: 'Storyboards',
      usageStorage: 'Storage',
      upgradeMonthly: 'Upgrade — {price}/mo',
      upgradeYearly: 'Upgrade — {price}/yr (save {saving}%)',
      checkoutPlanMonthly: 'Monthly',
      checkoutPlanYearly: 'Yearly',
      upgradeHeading: 'Upgrade to Yarnboard {plan}',
      priceMonthly: '{price} / month',
      priceYearly: '{price} / year ({monthly}/mo)',
      preparingCheckout: 'Preparing secure checkout…',
      confirmingSubscription: 'Confirming your subscription…',
      trialStarted: 'Your 30-day free trial has started! Unlimited notes, documents, AI tagging, Discovery and imports are unlocked. You won\'t be charged until the trial ends — cancel anytime before then in Settings.',
      done: 'Done',
      paymentFormLoadFailed: 'Failed to load the payment form',
      checkoutStartFailed: 'Could not start checkout',
      paymentFailed: 'Payment failed. Your card was not charged.',
      paymentNotConfirmed: 'Payment received but not yet confirmed — this can take a moment. Check Settings shortly.',
      confirmationFailed: 'Payment went through but confirmation failed — check Settings in a minute.',
    },
    // The two import dialogs opened from the document editor's File > Import menu — bringing outside files and Google Docs in as documents of their own. Kept out of `editor` because they are about getting documents into Yarnboard, not about editing one.
    importDocs: {
      importFailed: 'Import failed',
      localTitle: 'Import from Local Computer',
      dropzoneTitle: 'Drag and drop files here, or click to browse',
      supportedFormats: 'Supported formats:',
      selectedFiles: 'Selected Files',
      importing: 'Importing...',
      importFileOne: 'Import {count} file',
      importFileMany: 'Import {count} files',
      pickerLoadFailed: 'Failed to load Google Picker',
      pickerTitle: 'Select Google Docs to import',
      authCancelled: 'Google authorization was cancelled or failed',
      googleTitle: 'Import from Google Docs',
      loadingPicker: 'Loading Google Picker…',
      unconfiguredTitle: 'Google Docs import isn\'t set up yet',
      unconfiguredDesc: 'This feature needs a Google Picker API key and project number configured on the server.',
      googleHeading: 'Import your Google Docs',
      googleDesc: 'Pick the documents you want in Google\'s file browser. Yarnboard only ever sees the files you choose — nothing else in your Drive.',
      importingEllipsis: 'Importing…',
      chooseFromDrive: 'Choose from Google Drive',
      complete: 'Import complete',
      importedOne: 'Imported {count} of {total} document.',
      importedMany: 'Imported {count} of {total} documents.',
      imagesSkippedOne: '{count} image was left out — Google serves them from links that expire, so they have to be added again here.',
      imagesSkippedMany: '{count} images were left out — Google serves them from links that expire, so they have to be added again here.',
      failedOne: '{count} document couldn\'t be imported.',
      failedMany: '{count} documents couldn\'t be imported.',
      done: 'Done',
    },
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      confirm: 'Confirm',
      back: 'Back',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      documents: 'Documentos',
      visualize: 'Visualizar',
      discover: 'Descubrir',
      settings: 'Configuración',
      addNote: 'Agregar Nota',
      addDocument: 'Agregar Documento',
      tagManagement: 'Etiquetas',
      importDocuments: 'Importar Documentos',
      upgrade: 'Mejorar Plan',
      sharedWithMe: 'Compartido conmigo',
      notes: 'Notas',
      storyboards: 'Guiones gráficos',
      storyboardsTab: 'Guiones',
      shared: 'Compartido',
      about: 'Acerca de',
      feedback: 'Comentarios',
      addFolder: 'Agregar Carpeta',
      addStoryboard: 'Agregar Guion Gráfico',
    },
    home: {
      searchPlaceholder: 'Buscar entradas y documentos...',
      loading: 'Cargando...',
      noEntries: 'No se encontraron entradas',
      noDocuments: 'No se encontraron documentos',
      createFirstNote: 'Crea tu primera nota para comenzar',
      createFirstDocument: 'Crea tu primer documento para comenzar',
      selectEntries: 'Selecciona entradas para crear un documento',
      cancel: 'Cancelar',
      next: 'Siguiente',
      selected: 'seleccionados',
      createDocumentFromNotes: 'Crear Documento desde Notas',
      newFolder: 'Nueva Carpeta',
      untitledNote: 'Nota sin título',
      subfolderNamePrompt: 'Nombre de la subcarpeta:',
      folderCreateFailed: 'No se pudo crear la carpeta — puede que ya exista una con ese nombre acá.',
      searchDocumentsPlaceholder: 'Buscar documentos...',
      searchNotesPlaceholder: 'Buscar notas...',
      includeRelated: 'Incluir notas relacionadas',
      includeRelatedHint: 'Mostrá también las notas conectadas por etiquetas compartidas, después de las coincidencias directas',
      createNewFolder: 'Crear carpeta nueva',
      noNotes: 'No se encontraron notas',
      tryDifferentSearch: 'Probá con otra búsqueda',
      clickAddNote: 'Hacé clic en «Agregar Nota» para comenzar',
      relatedNotes: 'Notas relacionadas — conectadas por etiquetas compartidas',
      closeMatch: 'Coincidencia aproximada',
      matchedTag: 'Etiqueta coincidente',
      connectedBy: 'Conectada por:',
      inPdf: 'En el PDF',
      loadingMoreNotes: 'Cargando más notas...',
      importLocal: 'Este dispositivo',
      importLocalDesc: 'Subí archivos desde tu dispositivo',
      importGoogleDocs: 'Google Docs',
      importGoogleDocsDesc: 'Importar desde Google Drive',
      suggestionsTags: 'Etiquetas',
      suggestionsRecent: 'Recientes',
      removeFromHistory: 'Quitar {query} del historial',
      clearSearch: 'Borrar búsqueda',
    },
    note: {
      edit: 'Editar',
      delete: 'Eliminar',
      viewImage: 'Ver Imagen',
      tags: 'Etiquetas',
      links: 'Enlaces',
      createdOn: 'Creado',
      clickToCopy: 'Hacé clic para copiar.',
      clickToOpenPdf: 'Hacé clic para abrir el PDF.',
      openPdf: 'Abrir PDF',
      downloadPdf: 'Descargar PDF',
      seeThreads: 'Ve los hilos',
      viewMore: 'Ver más',
      close: 'Cerrar',
      confirmDelete: '¿Seguro que querés eliminar esta nota?',
      imageCopied: 'Imagen copiada al portapapeles.',
      textCopied: 'Texto copiado al portapapeles.',
      mediaAlt: 'Contenido de la nota',
      loadingImage: 'Cargando imagen...',
      noImageFile: 'Sin archivo de imagen',
      noImageUrl: 'Sin URL de imagen',
      fullSizeAlt: 'Tamaño completo',
    },
    document: {
      open: 'Abrir',
      rename: 'Renombrar',
      delete: 'Eliminar',
      sources: 'fuentes',
      lastModified: 'Última modificación',
      createdOn: 'Creado',
      deleteDocument: 'Eliminar documento',
      confirmDeleteDocument: '¿Eliminar "{name}"? No se puede deshacer.',
      selectedCount: '{count} seleccionados',
      clearSelection: 'Quitar selección',
      dragSelectionHint: 'Arrastrá cualquiera de ellos a una carpeta para moverlos todos',
      deleteSelected: 'Eliminar documentos',
      confirmDeleteSelected: '¿Eliminar {count} documentos? No se puede deshacer.',
    },
    folder: {
      createFolder: 'Crear Nueva Carpeta',
      newFolder: 'Nueva Carpeta',
      folderName: 'Nombre de Carpeta',
      enterFolderName: 'Ingresá el nombre de la carpeta...',
      create: 'Crear Carpeta',
      creating: 'Creando...',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      deleteFolder: 'Eliminar Carpeta',
      cannotDeleteWithDocs: 'No se puede eliminar la carpeta porque contiene documentos. Por favor movelos o eliminalos primero.',
      confirmDelete: '¿Estás seguro de que querés eliminar la carpeta',
      confirmDeleteMessage: 'Esta acción no se puede deshacer.',
      noDocuments: 'No hay documentos en esta carpeta',
      dragDropHint: 'Arrastrá y soltá documentos acá para organizarlos',
      expandFolder: 'Expandir carpeta',
      collapseFolder: 'Contraer carpeta',
      confirmDeleteFolder: '¿Eliminar la carpeta "{name}"? No se puede deshacer.',
      folderHolds: '"{name}" contiene {contents}.',
      documentsMoveToTop: 'Los documentos pasan al nivel principal.',
      subfoldersRemoved: 'Sus subcarpetas se eliminan y sus documentos pasan al nivel principal.',
      alsoDeleteDocuments: 'Eliminar también {count} que contiene',
      alsoDeleteDocumentsHint: 'Es permanente: los documentos se pierden para siempre.',
      typeToConfirm: 'Escribí {word} para confirmar',
      deleting: 'Eliminando...',
      documentOne: 'documento',
      documentMany: 'documentos',
      subfolderOne: 'subcarpeta',
      subfolderMany: 'subcarpetas',
    },
    tagManagement: {
      title: 'Gestión de Etiquetas',
      searchTags: 'Buscar etiquetas...',
      sortBy: 'Ordenar por',
      nameAZ: 'Nombre A-Z',
      nameZA: 'Nombre Z-A',
      mostUsed: 'Más Usadas',
      leastUsed: 'Menos Usadas',
      color: 'Color',
      textColor: 'Color de Texto',
      selected: 'seleccionadas',
      mergeSelected: 'Combinar Seleccionadas',
      deleteSelected: 'Eliminar Seleccionadas',
      totalTags: 'Total de Etiquetas',
      showing: 'Mostrando',
      totalEntries: 'Total de Entradas',
      entries: 'entradas',
      edit: 'Editar',
      editTag: 'Editar Etiqueta',
      name: 'Nombre',
      tagName: 'Nombre de etiqueta',
      saveChanges: 'Guardar Cambios',
      cancel: 'Cancelar',
      mergeTags: 'Combinar Etiquetas',
      mergeInto: 'Combinar en',
      confirmMerge: 'Confirmar Combinación',
      confirmMergeMessage: 'Esto combinará las siguientes etiquetas',
      cannotUndo: 'Esta acción no se puede deshacer. ¿Continuar?',
      confirmDelete: 'Confirmar Eliminación',
      confirmDeleteMessage: '¿Eliminar {count} etiquetas?',
      willRemoveTags: 'Esto eliminará estas etiquetas de {count} entradas.',
      deleteTags: 'Eliminar Etiquetas',
      selectAtLeast2: 'Por favor seleccioná al menos 2 etiquetas para combinar',
      selectTagsToDelete: 'Por favor seleccioná etiquetas para eliminar',
      loadingTags: 'Cargando etiquetas...',
      enterTagName: 'Ingresá el nombre de la etiqueta',
      preview: 'vista previa',
      failedToUpdate: 'No se pudo actualizar la etiqueta',
      failedToMerge: 'No se pudieron combinar las etiquetas',
      failedToDelete: 'No se pudieron eliminar las etiquetas',
    },
    settings: {
      title: 'Configuración',
      account: 'Cuenta',
      appearance: 'Apariencia',
      language: 'Idioma',
      help: 'Ayuda',
      about: 'Acerca de',
      accountInfo: 'Información de la Cuenta',
      name: 'Nombre',
      displayName: 'Nombre para Mostrar',
      enterName: 'Ingresa tu nombre',
      updateName: 'Actualizar Nombre',
      email: 'Correo Electrónico',
      emailLabel: 'Correo Electrónico',
      currentEmail: 'Correo Actual',
      changeEmail: 'Cambiar Correo',
      newEmail: 'Nuevo Correo',
      enterNewEmail: 'Ingresa nuevo correo',
      updateEmail: 'Actualizar Correo',
      updating: 'Actualizando...',
      password: 'Contraseña',
      changePassword: 'Cambiar Contraseña',
      currentPassword: 'Contraseña Actual',
      enterCurrentPassword: 'Ingresa contraseña actual',
      newPassword: 'Nueva Contraseña',
      enterNewPassword: 'Ingresa nueva contraseña',
      confirmPassword: 'Confirmar Contraseña',
      confirmNewPassword: 'Confirma nueva contraseña',
      updatePassword: 'Actualizar Contraseña',
      subscription: 'Suscripción',
      subscriptionPlaceholder: 'Gestión de suscripción próximamente.',
      logout: 'Cerrar Sesión',
      logoutButton: 'Cerrar Sesión',
      selectTheme: 'Tema',
      darkTheme: 'Modo Oscuro',
      lightTheme: 'Modo Claro',
      selectLanguage: 'Selecciona tu idioma preferido',
      english: 'English',
      spanish: 'Español (Latinoamérica)',
      portuguese: 'Português (Brasil)',
      feedbackTitle: 'Enviar comentarios',
      feedbackDesc: '¿Encontraste un error o tienes una idea? Envíalo directamente al desarrollador.',
      feedbackTypeBug: 'Error',
      feedbackTypeIdea: 'Idea / solicitud',
      feedbackTypeOther: 'Otro',
      feedbackPlaceholder: 'Describe el error o la idea…',
      feedbackSend: 'Enviar comentarios',
      feedbackSending: 'Enviando…',
      feedbackThanks: '¡Gracias! Tus comentarios fueron enviados.',
      feedbackError: 'No se pudo enviar. Inténtalo de nuevo.',
      helpSupport: 'Ayuda y Soporte',
      searchHelp: 'Buscar artículos de ayuda...',
      searchButton: 'Buscar',
      helpPlaceholder: 'Artículos de ayuda y tutoriales en video próximamente.',
      aboutApp: 'Acerca de Yarnboard',
      version: 'Versión 1.0.0',
      aboutDescription: 'Yarnboard es una potente aplicación de notas y gestión de documentos.',
      aboutPlaceholder: 'Más información próximamente.',
      dangerZone: 'Zona de Peligro',
      deactivateAccount: 'Desactivar Cuenta',
      deactivateWarning: 'Desactivar tu cuenta la deshabilitará temporalmente. Puedes reactivarla iniciando sesión nuevamente.',
      deactivate: 'Desactivar Cuenta',
      deleteAccount: 'Eliminar Cuenta',
      deleteWarning: 'Esta acción no se puede deshacer. Todos tus datos serán eliminados permanentemente.',
      deleteAccountButton: 'Eliminar Cuenta Permanentemente',
      emailUpdated: 'Correo actualizado exitosamente',
      nameUpdated: 'Nombre actualizado exitosamente',
      passwordUpdated: 'Contraseña actualizada exitosamente',
      passwordChanged: 'Contraseña cambiada exitosamente',
      themeUpdated: 'Tema actualizado exitosamente',
      languageUpdated: 'Idioma actualizado exitosamente',
      passwordMismatch: 'Las nuevas contraseñas no coinciden',
      passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
      updateFailed: 'Error al actualizar',
      failedToUpdate: 'Error al actualizar',
      failedToLoad: 'Error al cargar configuración',
      failedToChange: 'Error al cambiar contraseña',
      loading: 'Cargando...',
      currentPasswordPrompt: 'Ingresá tu contraseña actual para cambiar tu correo:',
      confirmDeactivate: '¿Seguro que querés desactivar tu cuenta? Podés reactivarla iniciando sesión de nuevo.',
      failedToDeactivate: 'No se pudo desactivar la cuenta',
      deletePrompt: 'Escribí «DELETE» para eliminar tu cuenta y todos tus datos de forma permanente:',
      failedToDeleteAccount: 'No se pudo eliminar la cuenta',
      moreThemesSoon: 'Más temas de color próximamente.',
    },
    addNote: {
      title: 'Agregar Nota',
      cancel: 'Cancelar',
      remember: 'Recordar',
      content: 'Contenido',
      contentPlaceholder: '¿Qué te gustaría recordar?',
      tags: 'Etiquetas',
      tagsPlaceholder: 'Agregar etiquetas...',
      links: 'Enlaces',
      addLink: 'Agregar Enlace',
      linkUrl: 'URL',
      linkTitle: 'Título (opcional)',
      image: 'Imagen',
      uploadImage: 'Subir Imagen',
      pasteImage: 'o pegar desde el portapapeles',
      error: 'Error',
      aiSuggest: 'Etiquetado automático',
      generatingTags: 'Generando etiquetas...',
      tagSuggestionFailed: 'Error al generar etiquetas',
      noTextInImage: 'No se encontró texto en la imagen',
      aiOffNeedsTag: 'Agregá al menos una etiqueta, o volvé a activar el etiquetado automático.',
      summarizeVideo: 'Resumir el video de YouTube',
      summarizeVideoDisabled: 'Agregá un link de YouTube para resumirlo',
      summarizingVideo: 'Mirando el video...',
      videoSummaryHeading: 'Resumen del video',
      videoSummaryFailed: 'No se pudo resumir el video',
      saving: 'Guardando...',
      pasteImageFailed: 'No se pudo procesar la imagen pegada',
      loadNoteFailed: 'No se pudieron cargar los datos de la nota',
      unsupportedFile: 'Tipo de archivo no admitido',
      fileFailed: 'No se pudo procesar el archivo',
      ocrFailed: 'No se pudo extraer el texto de la imagen',
      addContentFirst: 'Agregá algo de contenido antes de generar etiquetas',
      contentRequired: 'Hace falta contenido o una imagen.',
      tagRequiredFree: 'Agregá al menos una etiqueta para guardar tu nota.',
      tagRequired: 'Hace falta al menos una etiqueta.',
      saveFailed: 'No se pudo guardar la nota',
      addAttachment: 'Agregar imagen, video o PDF',
      extractText: 'Extraer texto de la imagen',
      addSourceUrls: 'Agregar URLs de origen',
      aiInfo: 'Los resultados de la IA pueden variar. Para que salgan mejor, agregá primero algunas etiquetas tuyas que guíen las sugerencias.',
      codeBlockHint: 'Bloque de código (o escribí ```)',
      equation: 'Ecuación',
      equationHint: 'Ecuación (o escribí $x^2$)',
      attachedAlt: 'Adjunto',
      removeImage: 'Quitar imagen',
      removePdf: 'Quitar PDF',
      codeLanguage: 'Lenguaje del código',
      latexPrompt: 'LaTeX',
      latexInsertPrompt: 'LaTeX (por ejemplo E = mc^2)',
    },
    editNote: {
      title: 'Editar Nota',
      update: 'Actualizar',
    },
    addDocument: {
      title: 'Crear Nuevo Documento',
      documentName: 'Nombre del Documento',
      documentNamePlaceholder: 'Nombre del Documento',
      create: 'Crear',
      creating: 'Creando...',
      cancel: 'Cancelar',
      nameRequired: 'El nombre del documento es requerido.',
      failedToCreate: 'Error al crear documento',
    },
    editor: {
      untitled: 'Documento Sin Título',
      saving: 'Guardando...',
      saved: 'Guardado',
      unsaved: 'Cambios sin guardar',
      share: 'Compartir',
      info: 'Información del Documento',
      back: 'Volver',
      convertToEntry: 'Convertir documento a entrada única',
      convertToFootnote: 'Convertir selección a nota al pie',
      file: 'Archivo',
      edit: 'Editar',
      insert: 'Insertar',
      format: 'Formato',
      view: 'Ver',
      tools: 'Herramientas',
      help: 'Ayuda',
      print: 'Imprimir',
      undo: 'Deshacer',
      redo: 'Rehacer',
      selectAll: 'Seleccionar Todo',
      bold: 'Negrita',
      italic: 'Cursiva',
      underline: 'Subrayado',
      strikethrough: 'Tachado',
      textColor: 'Color de texto',
      highlightColor: 'Color de resaltado',
      fontSize: 'Tamaño de fuente',
      decreaseFontSize: 'Reducir tamaño de fuente',
      increaseFontSize: 'Aumentar tamaño de fuente',
      heading: 'Encabezado',
      heading1: 'Encabezado 1',
      heading2: 'Encabezado 2',
      heading3: 'Encabezado 3',
      heading4: 'Encabezado 4',
      heading5: 'Encabezado 5',
      heading6: 'Encabezado 6',
      paragraph: 'Párrafo',
      bulletList: 'Lista con viñetas',
      numberedList: 'Lista numerada',
      checkList: 'Lista de tareas',
      alignLeft: 'Alinear a la izquierda',
      alignCenter: 'Alinear al centro',
      alignRight: 'Alinear a la derecha',
      alignJustify: 'Justificar',
      indent: 'Aumentar sangría',
      outdent: 'Reducir sangría',
      link: 'Enlace',
      image: 'Imagen',
      table: 'Tabla',
      horizontalRule: 'Línea horizontal',
      blockquote: 'Cita',
      codeBlock: 'Bloque de código',
      clearFormatting: 'Limpiar formato',
      builtFrom: 'Armado con estas notas',
      sourcesChanged: 'Una nota con la que se escribió esto cambió desde entonces.',
      sourceChanged: 'cambió',
      writerNote: 'Nota para vos',
      writerNoteHint: 'Nota para vos — no sale en nada que exportes',
      versionHistory: 'Historial de versiones',
      versionHistoryIntro: 'Yarnboard guarda las últimas 15 versiones de este documento mientras escribes, para que puedas recuperar lo que se borre o cambie por error.',
      versionsLoading: 'Cargando versiones…',
      noVersionsYet: 'Aún no hay versiones anteriores. Se guardan mientras escribes.',
      versionWords: 'palabras',
      restoreVersion: 'Restaurar',
      restoreConfirm: '¿Reemplazar el documento con esta versión? Lo que hay ahora quedará en el historial, así que puedes deshacerlo.',
      restoreFailed: 'No se pudo restaurar esta versión. Inténtalo de nuevo.',
      loadingDocument: 'Cargando documento...',
      loadFailed: 'No se pudo cargar el documento',
      saveFailed: 'No se pudo guardar el documento',
      backToEntryForm: 'Volver al formulario de nota',
      importMenu: 'Importar',
      importLocalComputer: 'Este dispositivo',
      download: 'Descargar',
      downloadPdf: 'PDF (.pdf)',
      downloadWord: 'Word (.docx)',
      downloadRichText: 'Texto enriquecido (.rtf)',
      downloadWebPage: 'Página web (.html)',
      cut: 'Cortar',
      copy: 'Copiar',
      paste: 'Pegar',
      withoutFormatting: '(sin formato)',
      showRuler: 'Mostrar regla',
      printLayout: 'Diseño de impresión',
      fullScreen: 'Pantalla completa',
      text: 'Texto',
      superscript: 'Superíndice',
      subscript: 'Subíndice',
      upperCase: 'MAYÚSCULAS',
      lowerCase: 'minúsculas',
      titleCase: 'Tipo Título',
      alignAndIndent: 'Alinear y sangría',
      left: 'Izquierda',
      center: 'Centro',
      right: 'Derecha',
      justify: 'Justificar',
      increaseIndent: 'Aumentar sangría',
      decreaseIndent: 'Reducir sangría',
      script: 'Guion',
      scriptScene: 'Encabezado de escena',
      scriptAction: 'Acción',
      scriptCharacter: 'Personaje',
      scriptDialogue: 'Diálogo',
      scriptParenthetical: 'Acotación',
      scriptTransition: 'Transición',
      scriptNone: 'Texto normal',
      scriptHint: 'Tab cambia de elemento · Enter sigue',
      scriptHintNote: '(personaje→diálogo…)',
      bulletsAndNumbering: 'Viñetas y numeración',
      lineAndSpacing: 'Interlineado y espaciado',
      spacingSingle: 'Sencillo',
      spacingDouble: 'Doble',
      addSpaceBefore: 'Agregar espacio antes del párrafo',
      removeSpaceBefore: 'Quitar espacio antes del párrafo',
      customSpacingMenu: 'Espaciado personalizado...',
      keepWithNext: 'Mantener con el siguiente',
      keepLinesTogether: 'Mantener líneas juntas',
      preventSingleLines: 'Evitar líneas sueltas',
      addPageBreakBefore: 'Agregar salto de página antes',
      insertRowAbove: 'Insertar fila arriba',
      insertRowBelow: 'Insertar fila abajo',
      insertColumnLeft: 'Insertar columna a la izquierda',
      insertColumnRight: 'Insertar columna a la derecha',
      toggleHeaderRow: 'Alternar fila de encabezado',
      deleteRow: 'Eliminar fila',
      deleteColumn: 'Eliminar columna',
      deleteTable: 'Eliminar tabla',
      wordCount: 'Conteo de palabras',
      proofreadSettings: 'Ajustes de revisión',
      infoSourceEntries: 'Notas de origen:',
      infoTags: 'Etiquetas:',
      infoLinks: 'Enlaces:',
      tags: 'Etiquetas',
      links: 'Enlaces',
      textStyle: 'Estilo de texto',
      screenplayElementHint: 'Elemento de guion — Tab cambia, Enter pasa al siguiente',
      notScript: 'Sin guion',
      font: 'Fuente',
      defaultColor: 'Color predeterminado',
      noHighlight: 'Sin resaltado',
      textOutline: 'Contorno del texto',
      themeDefault: 'Color del tema',
      removeOutline: 'Quitar contorno',
      insertImageOrPaste: 'Insertar imagen (o pegar)',
      customColor: 'Color personalizado',
      custom: 'Personalizado',
      pageSingle: 'Página 1',
      pageRange: 'Páginas 1–{count}',
      pages: 'Páginas',
      words: 'Palabras',
      characters: 'Caracteres',
      charactersNoSpaces: 'Caracteres sin espacios',
      displayWordCountWhileTyping: 'Mostrar el conteo de palabras mientras escribes',
      wordsLabel: 'Palabras:',
      proofreadIntro: 'Elige qué revisiones quieres activar mientras escribes:',
      proofSpelling: 'Ortografía',
      proofSpellingDesc: 'Subraya en rojo las palabras mal escritas',
      proofGrammar: 'Gramática',
      proofGrammarDesc: 'Subraya en azul los problemas de gramática',
      proofActiveVoice: 'Voz activa',
      proofActiveVoiceDesc: 'Detecta la voz pasiva y sugiere alternativas en voz activa',
      proofConciseness: 'Concisión',
      proofConcisenessDesc: 'Sugiere alternativas más cortas y quita la palabrería',
      proofSentenceSplit: 'División de oraciones',
      proofSentenceSplitDesc: 'Identifica las oraciones demasiado complejas',
      noteLabel: 'Nota:',
      proofreadNote: 'La ortografía se revisa en español latinoamericano, portugués de Brasil e inglés, según el idioma de cada párrafo. Haz clic derecho en una palabra subrayada para ver sugerencias o agregarla a tu diccionario.',
      spellAddToDictionary: 'Agregar al diccionario',
      spellIgnore: 'Ignorar',
      spellNoSuggestions: 'Sin sugerencias',
      customSpacing: 'Espaciado personalizado',
      lineSpacing: 'Interlineado',
      paragraphSpacingPts: 'Espaciado del párrafo (pts)',
      spacingBefore: 'Antes',
      spacingAfter: 'Después',
      apply: 'Aplicar',
      placeholder: 'Empieza a escribir tu documento...',
      clipboardBlockedCut: 'El navegador bloqueó el cortar — usa ⌘X / Ctrl+X.',
      clipboardBlockedCopy: 'El navegador bloqueó el copiar — usa ⌘C / Ctrl+C.',
      clipboardBlockedPaste: 'El navegador bloqueó el pegar — usa ⌘V / Ctrl+V.',
      linkUrlPrompt: 'URL del enlace (déjalo vacío para quitar el enlace):',
      imagePasteFailed: 'No se pudo subir la imagen pegada. Inténtalo de nuevo.',
      imageUploadFailed: 'No se pudo subir la imagen. Inténtalo de nuevo.',
      exportFailed: 'La exportación falló. Inténtalo de nuevo.',
    },
    orgDoc: {
      title: 'Organizar Documento',
      documentName: 'Nombre del Documento',
      namePlaceholder: 'Ingresa el nombre del documento...',
      saving: 'Guardando...',
      instructions: 'Arrastra las entradas para reordenarlas',
      entries: 'Entradas',
      preview: 'Vista Previa',
      stats: 'Estadísticas del Documento',
      totalEntries: 'Total de Entradas',
      totalTags: 'Total de Etiquetas',
      totalLinks: 'Total de Enlaces',
      combinedTags: 'Etiquetas Combinadas',
      combinedLinks: 'Enlaces Combinados',
      contentPreview: 'Vista Previa del Contenido',
      cancel: 'Cancelar',
      finalize: 'Finalizar Documento',
    },
    sharing: {
      share: 'Compartir',
      shareTitle: 'Compartir “{name}”',
      peopleWithAccess: 'Personas con acceso',
      onlyYou: 'Por ahora solo tú tienes acceso.',
      inviteByEmail: 'Invitar por correo',
      emailPlaceholder: 'nombre@ejemplo.com',
      invite: 'Invitar',
      inviting: 'Invitando…',
      canEdit: 'Puede editar',
      canView: 'Puede ver',
      pending: 'Pendiente',
      pendingExplain: 'Pendiente significa que aún no ha abierto la invitación. El acceso empieza cuando abre el enlace e inicia sesión.',
      emailedLink: 'Le enviamos el enlace por correo.',
      sendThemLink: 'Envíale este enlace.',
      copyLink: 'Copiar enlace',
      copied: 'Copiado',
      remove: 'Quitar',
      removeConfirm: '¿Dejar de compartir con {name}?',
      loadFailed: 'No se pudo cargar quién tiene acceso.',
      close: 'Cerrar',
      sharedBy: 'Compartido por {name}',
      youCanEdit: 'Puedes editar',
      youCanView: 'Puedes ver',
      lastEditedBy: 'Última edición de {name}',
      viewOnly: 'Solo lectura',
      othersNote: 'Nota de {name}',
      sharedWithMeTitle: 'Compartido conmigo',
      nothingShared: 'Todavía nadie ha compartido nada contigo.',
      kindNote: 'Notas',
      kindDocument: 'Documentos',
      kindStoryboard: 'Guiones gráficos',
      updated: 'Actualizado {date}',
      leave: 'Salir',
      leaveConfirm: '¿Salir de “{name}”? Perderás el acceso hasta que vuelvan a compartirlo contigo.',
      invitedYouEdit: '{name} te invitó a editar “{title}”',
      invitedYouView: '{name} te invitó a ver “{title}”',
      inviteSignIn: 'Iniciar sesión',
      inviteCreateAccount: 'Crear cuenta',
      inviteAccept: 'Aceptar invitación',
      inviteAccepting: 'Abriendo…',
      inviteSignedOutHint: 'Inicia sesión con {email}, o crea una cuenta con ese correo, y volverás directo aquí.',
      inviteUsed: 'Esta invitación ya fue usada por otra persona.',
      inviteInvalid: 'Esta invitación ya no es válida.',
      inviteFailed: 'No se pudo aceptar esta invitación.',
      conflictTitle: 'Alguien guardó antes que tú',
      conflictBody: '{name} guardó una versión más nueva mientras escribías.',
      conflictLoadTheirs: 'Cargar su versión',
      conflictKeepMine: 'Conservar la mía',
      conflictWarning: 'Cargar su versión reemplaza lo que tienes en pantalla. Conservar la tuya guarda sobre la de esa persona.',
    },
    // The document explorer's toolbar and folder rows
    explorer: {
      newSubfolder: 'Nueva subcarpeta',
      newDocumentInFolder: 'Nuevo documento en esta carpeta',
      name: 'Nombre',
      modified: 'Modificado',
      created: 'Creado',
      grouping: 'Agrupación',
      foldersFirst: 'Carpetas primero',
      filesFirst: 'Archivos primero',
      mixed: 'Mezclado',
    },
    // The tag field on the note composer
    tagInput: {
      label: 'Etiquetas',
      required: '· obligatorio',
      editBackgroundColor: 'Editar color de fondo',
      editTextColor: 'Editar color del texto',
      removeTag: 'Quitar etiqueta',
      addAnother: 'Agregar otra…',
      addTags: 'Agregá etiquetas… presioná Enter',
      recentClickToAdd: 'Recientes · hacé clic para agregar',
    },
    // Source and external links, on the note composer and the note form
    links: {
      sourceUrls: 'URLs de origen',
      externalLinks: 'Enlaces Externos',
      enterUrl: 'Ingresá la URL...',
      typeReference: 'Referencia',
      typeVideo: 'Video',
      typeMeme: 'Meme',
      add: 'Agregar',
      removeLink: 'Quitar enlace',
      collapseSection: 'Contraer sección',
    },
    // The in-app About page, including its privacy policy. Bodies use **bold** and *italic* markers, rendered by About.tsx.
    about: {
      tagline: 'Uní. Los. Puntos.',
      whatIsTitle: '¿Qué es esta cosa?',
      whatIsP1: '¿Conocés esa sensación de que *sabés* que viste algo —un meme, un tuit, ese video— y no lo podés encontrar? Ahí estás, scrolleando meses de caos para rearmar **ese argumento perfecto** que tenías guardado en la cabeza.',
      whatIsP2: '**Bienvenido a Yarnboard.** Esto no es una app de notas más. Es tu *muro de conspiración digital*. Con hilo rojo y todo. Solo que el hilo rojo de verdad es inteligente, las chinches se pueden buscar y nada se cae al piso a las 3 de la mañana.',
      whoForTitle: '¿Para quién es esto?',
      whoForIntro: 'Para vos. Vos en particular. Sobre todo si sos:',
      whoFor1: '**Periodista de investigación** siguiendo fuentes por una docena de madrigueras',
      whoFor2: '**Documentalista** ahogándote en marcas de tiempo y citas de entrevistas',
      whoFor3: '**Escritor** que se niega a perder esa frase perfecta que se le ocurrió en la ducha',
      whoFor4: '**Investigador** administrando citas como si defendieras una tesis ante un tribunal especialmente hostil',
      whoFor5: '**Youtuber** que necesita citar fuentes antes de que alguien te corrija en los comentarios',
      whoFor6: '**Docente** armando clases con PDFs sueltos y artículos guardados en favoritos',
      whoFor7: '**Comediante** guardando premisas de chistes y esa respuesta perfecta para después',
      whoFor8: '**Alguien que enseña a leer medios** rastreando desinformación como si cazaras a Pie Grande (solo que Pie Grande existe y se llama desinformación)',
      whoFor9: '**Adicto a Twitter/Bluesky** que necesita recordar al instante ese hilo de hace tres semanas',
      whoFor10: '**Abogado** que *definitivamente* no quiere perderle el rastro a una prueba',
      whoFor11: '**Entrenador** organizando material de entrenamiento y frases motivacionales',
      whoFor12: '**Cualquiera** que alguna vez dijo «te juro que esto lo vi en algún lado…»',
      howTitle: 'La conspiración (cómo funciona)',
      howIntro: 'Imaginate un **muro de hilos literal** —esos donde los detectives clavan fotos y las unen con hilo rojo—. Ahora imaginate que ese muro es *infinito*, que el hilo se ata solo y que podés buscar en todo él escribiendo tres letras.',
      featBrainTitle: '🧠 Tu cerebro externo',
      featBrainBody: 'Cada meme. Cada captura. Cada momento de «esto lo tengo que recordar». Guardalo acá. El OCR lee el texto de las imágenes, así que podés encontrar esa captura de un tuit de 2022 escribiendo «clima» o «ratio» o como se diga ahora.',
      featTagsTitle: '🏷️ Todo lleva etiqueta',
      featTagsBody: '¿Sin etiqueta? Sin entrada. No te vamos a dejar convertirte en acumulador digital. Todo necesita al menos una etiqueta. La IA te ayuda a etiquetar sola, porque de verdad *lee* lo que estás guardando. Tu yo del futuro te lo va a agradecer cuando encuentres eso en 2 segundos y no en 2 horas.',
      featWebTitle: '🕸️ La red de conexiones',
      featWebBody: 'Un mapa mental visual que dibuja conexiones que ni sabías que existían. ¿Esa cita de un podcast? Se relaciona con estos tres artículos, ese documental y este hilo de Twitter. **Los puntos se unen solos**. De nada.',
      featRecallTitle: '🎯 Memoria instantánea',
      featRecallBody: '«Esto lo repito siempre en los comentarios…» Buscá, tocá, copiá, pegá. Listo. ¿La cita que necesitás? ¿La respuesta perfecta? ¿El link del video que te pidieron? Está acá. Siempre.',
      featVideoTitle: '📹 ¿Video? Transcrito.',
      featVideoBody: 'Pegá un link de YouTube. Yarnboard saca el audio, lo transcribe y lo vuelve buscable. Así, cuando alguien pregunte «¿qué era eso del video?», podés responder de verdad en vez de decir «era… eh… después lo busco» (no lo vas a buscar).',
      featVoiceTitle: '🎙️ Notas de voz que no se pierden',
      featVoiceBody: 'Grabá notas de voz. Se transcriben y se guardan. Porque a veces las mejores ideas llegan manejando y no las podés anotar sin provocar un accidente.',
      featDocsTitle: '📄 Documentos a partir del caos',
      featDocsBody: 'Juntá entradas sueltas, buscá en tu descarga mental y armá documentos de verdad. Dividí las entradas cuando son demasiado amplias. Combinalas cuando son demasiado específicas. Arrastrá, soltá, editá, reordená. Es como LEGO, pero de información.',
      featSourcesTitle: '🔗 Todas tus fuentes, ordenadas',
      featSourcesBody: 'Guardá links ilimitados en cada entrada. ¿Citas? ¿Referencias? ¿«Dónde vi esto»? Está todo ahí. Abogados e investigadores: esto les va a servir *especialmente*.',
      featImportTitle: '📥 Importá todo',
      featImportBody: '¿Tenés documentos hechos? ¿PDFs? ¿Google Docs? Tiralos acá. La IA los lee, los etiqueta y los integra a tu red de conocimiento. Tus notas viejas ya no son inútiles: ahora están *conectadas*.',
      featExtensionTitle: '🌐 Próximamente: extensión de navegador',
      featExtensionBody: '¿Viste algo interesante? Un clic. Guardado. Etiquetado. Conectado. Es como recortar la web, pero para gente que de verdad *usa* sus recortes.',
      bottomTitle: 'En resumen',
      bottomP1: 'Yarnboard no es solo una app. Es un **muro de conspiración para tu cabeza**. Todo lo que ves, leés, mirás o pensás queda guardado, ordenado, conectado y buscable al instante. Cuando necesites encontrar esa cosa, la *vas* a encontrar.',
      bottomP2: 'Dejá de perder tus ideas. Empezá a unir los puntos.',
      bottomCta: '**Bienvenido a la conspiración. Ahora el detective sos vos.**',
      privacyTitle: 'Política de Privacidad',
      privacyUpdated: 'Última actualización: 8 de noviembre de 2025',
      infoCollectTitle: 'Información que recopilamos',
      infoCollectIntro: 'Yarnboard recopila y guarda la información que vos elegís guardar explícitamente en la aplicación, incluyendo:',
      infoCollect1: 'Entradas de texto, notas y documentos que creás',
      infoCollect2: 'Imágenes, capturas y archivos multimedia que subís',
      infoCollect3: 'Grabaciones de audio y sus transcripciones',
      infoCollect4: 'URLs y enlaces web que guardás',
      infoCollect5: 'Etiquetas y rótulos de organización que creás',
      infoCollect6: 'Enlaces de video y sus transcripciones',
      infoCollect7: 'Los datos de tu cuenta (correo, nombre de usuario, contraseña)',
      howUseTitle: 'Cómo usamos tu información',
      howUseIntro: 'Tus datos se usan únicamente para que Yarnboard funcione:',
      howUse1: 'Guardar y organizar el contenido que guardás',
      howUse2: 'Permitir buscar y recuperar tus entradas',
      howUse3: 'Crear conexiones visuales entre contenidos relacionados',
      howUse4: 'Procesar OCR en imágenes y transcribir audio y video',
      howUse5: 'Generar sugerencias de etiquetas con IA',
      storageTitle: 'Almacenamiento y seguridad de los datos',
      storageBody: 'Tu contenido se guarda de forma segura en servidores en la nube, con cifrado en tránsito y en reposo. Aplicamos medidas de seguridad estándar de la industria para proteger tus datos de accesos, modificaciones o eliminaciones no autorizadas.',
      thirdPartyTitle: 'Servicios de terceros',
      thirdPartyIntro: 'Yarnboard puede usar servicios de terceros para:',
      thirdParty1: 'Almacenamiento y alojamiento en la nube',
      thirdParty2: 'Procesamiento de OCR y transcripción',
      thirdParty3: 'Análisis de contenido con IA para el etiquetado',
      thirdPartyOutro: 'Esos servicios se rigen por sus propias políticas de privacidad y los elegimos según sus estándares de seguridad y privacidad.',
      sharingTitle: 'Compartir datos',
      sharingBody: '**No vendemos, alquilamos ni compartimos tus datos personales con terceros con fines de marketing.** Tu contenido sigue siendo privado y solo vos podés acceder a él desde tu cuenta.',
      rightsTitle: 'Tus derechos',
      rightsIntro: 'Tenés derecho a:',
      rights1: 'Acceder a todos los datos asociados a tu cuenta',
      rights2: 'Exportar tus datos en formatos estándar',
      rights3: 'Eliminar tus datos y tu cuenta cuando quieras',
      rights4: 'Pedir que se corrijan tus datos',
      retentionTitle: 'Conservación de los datos',
      retentionBody: 'Conservamos tus datos mientras tu cuenta esté activa. Si eliminás tu cuenta, todos los datos asociados se borran de forma permanente en un plazo de 30 días, salvo cuando la ley exija conservarlos.',
      cookiesTitle: 'Cookies y seguimiento',
      cookiesBody: 'Yarnboard usa cookies esenciales para la autenticación y la gestión de sesiones. No usamos cookies de seguimiento ni cookies publicitarias de terceros.',
      changesTitle: 'Cambios en esta política',
      changesBody: 'Podemos actualizar esta política de privacidad cada tanto. Los cambios importantes se comunican dentro de la aplicación o por correo. Seguir usando Yarnboard después de un cambio implica aceptar la política actualizada.',
      contactTitle: 'Contacto',
      contactBody: 'Si tenés dudas sobre esta política de privacidad o sobre tus datos, escribinos por los canales de soporte de la aplicación.',
      gdprTitle: 'Cumplimiento del RGPD',
      gdprBody: 'Para usuarios de la Unión Europea, Yarnboard cumple con los requisitos del RGPD. Tenés derecho a la portabilidad de los datos, al olvido y a limitar el tratamiento de tus datos personales.',
      ccpaTitle: 'Cumplimiento de la CCPA',
      ccpaBody: 'Para residentes de California, Yarnboard cumple con la Ley de Privacidad del Consumidor de California (CCPA). Tenés derecho a saber qué información personal se recopila, a que se elimine y a oponerte a su venta (aunque no vendemos información personal).',
      copyright: '© 2025 Yarnboard. Todos los derechos reservados.',
      version: 'Versión 1.0 (nombre clave: Hilo Rojo)',
    },
    // The "here's what you'd get" teaser a free user sees at a premium feature
    premium: {
      ocrTitle: 'Sacá el texto de cualquier imagen',
      ocrTagline: 'Sacá una foto de una página, un pizarrón o un recibo — Yarnboard la convierte en texto que podés editar.',
      ocrBullet1: 'Convertí capturas y fotos en notas que podés buscar',
      ocrBullet2: 'Ideal para páginas escritas a mano y documentos impresos',
      ocrBullet3: 'Un toque en la imagen — el texto cae directo en tu nota',
      aiTaggingTitle: 'Dejá que la IA etiquete tus notas',
      aiTaggingTagline: 'Yarnboard lee tu nota y sugiere las etiquetas justas, solo.',
      aiTaggingBullet1: 'Etiquetas consistentes sin trabajo repetitivo',
      aiTaggingBullet2: 'Reutiliza las etiquetas que ya tenés cuando encajan',
      aiTaggingBullet3: 'Mantiene tu tablero ordenado mientras crece',
      discoveryTitle: 'Explorá tus notas como un grafo vivo',
      discoveryTagline: 'Elegí cualquier nota y mirá cómo se ramifican las relacionadas — aparecen conexiones que ya ni recordabas.',
      discoveryBullet1: 'Las etiquetas compartidas más raras revelan los vínculos más sorprendentes',
      discoveryBullet2: 'Hacé clic en cualquier nodo para seguir ramificando',
      discoveryBullet3: 'Convertí una pila de notas en un mapa de tu pensamiento',
      videoSummaryTitle: 'Resumí un video de YouTube dentro de tu nota',
      videoSummaryTagline: 'Guardá el enlace, apretá el botón y los puntos del video caen en tu nota como texto que podés buscar.',
      videoSummaryBullet1: 'Todos los puntos importantes, en el orden en que los dice el video',
      videoSummaryBullet2: 'Se conservan nombres, números y fechas — es lo que vas a buscar después',
      videoSummaryBullet3: 'El resumen es texto de nota: editalo, etiquetalo, ponelo en un documento',
      importTitle: 'Importá los documentos que ya tenés',
      importTagline: 'Traé Google Docs y archivos de tu dispositivo (Word, PDF, Markdown) como documentos de Yarnboard que podés editar.',
      importBullet1: 'Elegí Google Docs directo desde Drive',
      importBullet2: 'Subí Word, PDF, RTF, Markdown y más',
      importBullet3: 'Todo queda completamente editable en Yarnboard',
      collaborationTitle: 'Escriban juntos',
      collaborationTagline: 'Comparte un guion gráfico, un documento o una sola nota con las personas con quienes trabajas.',
      collaborationBullet1: 'Invita a cualquiera por correo, para editar o solo para ver',
      collaborationBullet2: 'Sus notas quedan en tu tablero junto a las tuyas',
      collaborationBullet3: 'Dos personas en un documento nunca se sobrescriben en silencio',
      premiumFeature: 'Función premium',
      seePlans: 'Ver planes y mejorar',
      maybeLater: 'Quizá después',
    },
    // The Discover view, its free-plan teaser and the note graph
    discover: {
      teaserTitle: 'Explorá tus notas como un grafo vivo',
      teaserBody: 'Elegí cualquier nota y mirá cómo se ramifican las notas relacionadas, sacando a la luz conexiones que habías olvidado. Las etiquetas compartidas más raras revelan los vínculos más sorprendentes, y cada nodo se sigue expandiendo.',
      teaserLock: '🔒 Función premium',
      teaserCta: 'Ver planes y mejorar',
      title: 'Descubrí conexiones',
      hint: 'Elegí una nota para ver qué se relaciona',
      noTaggedNotes: 'No se encontraron notas con etiquetas',
      loadMore: 'Cargar más',
      noText: '(sin texto)',
      placeholderTitle: 'Descubrí conexiones ocultas',
      placeholderBody: 'Elegí una nota del panel de la izquierda. Se vuelve el centro de un grafo vivo: las notas relacionadas se ramifican a partir de ella, y al hacer clic en cualquier nodo se ramifica todavía más.',
      placeholderSub: 'Las conexiones vienen de las etiquetas compartidas. Cuantas menos notas tenga una etiqueta, más fuerte es la señal de conexión.',
      graphClickToBranch: 'Hacé clic para ramificar',
      graphEmpty: 'Las etiquetas de esta nota son solo suyas: todavía ninguna otra nota las comparte.',
      graphHint: 'Hacé clic en un nodo para ramificar · arrastrá para reordenar · desplazá para hacer zoom',
    },
    // The storyboard list and the storyboard editor
    storyboards: {
      title: 'Guiones gráficos',
      newStoryboard: 'Nuevo guion gráfico',
      loading: 'Cargando guiones gráficos...',
      noneYet: 'Todavía no hay guiones gráficos',
      createFirst: 'Creá tu primer guion gráfico para empezar a organizar tus entradas de forma visual',
      createStoryboard: 'Crear guion gráfico',
      createTitle: 'Crear nuevo guion gráfico',
      name: 'Nombre',
      namePlaceholder: 'Ingresá el nombre del guion gráfico...',
      descriptionOptional: 'Descripción (opcional)',
      descriptionPlaceholder: 'Ingresá una descripción...',
      create: 'Crear',
      entryOne: 'entrada',
      entryMany: 'entradas',
      updated: 'Actualizado {date}',
      loadFailed: 'No se pudieron cargar los guiones gráficos',
      nameRequired: 'El nombre del guion gráfico es obligatorio',
      createFailed: 'No se pudo crear el guion gráfico',
      deleteConfirm: '¿Seguro que querés eliminar este guion gráfico?',
      deleteFailed: 'No se pudo eliminar el guion gráfico',
      loadingOne: 'Cargando guion gráfico...',
      loadOneFailed: 'No se pudo cargar el guion gráfico',
      docCreateFailed: 'No se pudo crear el documento a partir del guion gráfico',
      noteSaveFailed: 'No se pudo guardar esa nota',
      addEntryFailed: 'No se pudo agregar la entrada',
      removeEntryFailed: 'No se pudo quitar la entrada',
      orderSaveFailed: 'No se pudo guardar el orden',
      removeEntryConfirm: '¿Quitar esta entrada del guion gráfico?',
      createDocument: 'Crear documento',
      createDocumentTooltip: 'Combiná las entradas de este guion gráfico, en orden, en un documento',
      addEntries: 'Agregar entradas',
      expand: 'Expandir',
      collapse: 'Contraer',
      searchPlaceholder: 'Buscá entradas por contenido o etiquetas...',
      newNoteTagged: 'Nueva nota etiquetada “{tag}”',
      searching: 'Buscando...',
      noContent: '(sin contenido)',
      addToStoryboard: 'Agregar al guion gráfico',
      searchPrompt: 'Buscá entradas para agregar a tu guion gráfico',
      searchPromptHint: '¿No tenés ninguna? Agregá algunas acá y después buscalas por la etiqueta que les pusiste.',
      boardHeading: 'Guion gráfico',
      empty: 'Tu guion gráfico está vacío',
      emptyHint: 'Buscá y agregá entradas para empezar',
      cardNotePlaceholder: '¿Para qué sirve este momento?',
      cardNoteTitle: 'Una nota para vos sobre este momento. Encabeza esta tarjeta en el borrador.',
      addCardNote: '+ nota',
      docDialogTitle: 'Crear documento a partir del guion gráfico',
      docDialogHint: 'Las {count} entradas se combinarán en el orden del guion gráfico.',
      docNamePlaceholder: 'Nombre del documento',
      creating: 'Creando…',
      createAndOpen: 'Crear y abrir',
    },
    // Subscription, usage limits and checkout
    billing: {
      planFree: 'Gratis',
      planMonthly: 'Mensual ({price}/mes)',
      planYearly: 'Anual ({price}/año)',
      planComp: 'Cortesía (acceso completo)',
      premiumBadge: 'PREMIUM',
      unlimited: 'ilimitado',
      loadFailed: 'No se pudo cargar la información de la suscripción',
      loading: 'Cargando…',
      cancelConfirm: '¿Cancelar tu suscripción? Mantenés el acceso premium hasta el final del período que ya pagaste.',
      cancelFailed: 'No se pudo cancelar',
      canceling: 'Cancelando…',
      cancelSubscription: 'Cancelar suscripción',
      pastDueWarning: 'Falló la última renovación — actualizá tu pago o el acceso termina el {date}.',
      canceledWarning: 'Cancelada — premium hasta el {date}.',
      switchToYearlyConfirm: '¿Cambiar al plan Anual ({price}/año — ahorrás {saving}%)? Tu plan mensual se cancela y el anual empieza ahora.',
      switchToMonthlyConfirm: '¿Cambiar al plan Mensual ({price}/mes)? Tu plan anual se cancela (el acceso corre hasta lo que ya pagaste) y el cobro mensual empieza ahora.',
      switchToYearly: 'Cambiar a Anual (ahorrá {saving}%)',
      switchToMonthly: 'Cambiar a Mensual',
      usageNotes: 'Notas',
      usageDocuments: 'Documentos',
      usageStoryboards: 'Guiones gráficos',
      usageStorage: 'Almacenamiento',
      upgradeMonthly: 'Mejorar — {price}/mes',
      upgradeYearly: 'Mejorar — {price}/año (ahorrá {saving}%)',
      checkoutPlanMonthly: 'Mensual',
      checkoutPlanYearly: 'Anual',
      upgradeHeading: 'Mejorá a Yarnboard {plan}',
      priceMonthly: '{price} / mes',
      priceYearly: '{price} / año ({monthly}/mes)',
      preparingCheckout: 'Preparando el pago seguro…',
      confirmingSubscription: 'Confirmando tu suscripción…',
      trialStarted: '¡Empezó tu prueba gratis de 30 días! Ya tenés notas y documentos ilimitados, etiquetado con IA, Descubrimiento e importaciones. No te cobramos hasta que termine la prueba — cancelá cuando quieras antes de eso en Configuración.',
      done: 'Listo',
      paymentFormLoadFailed: 'No se pudo cargar el formulario de pago',
      checkoutStartFailed: 'No se pudo iniciar el pago',
      paymentFailed: 'Falló el pago. No se hizo ningún cargo a tu tarjeta.',
      paymentNotConfirmed: 'Recibimos el pago pero todavía no está confirmado — esto puede tardar un momento. Revisá Configuración en un rato.',
      confirmationFailed: 'El pago se hizo pero falló la confirmación — revisá Configuración en un minuto.',
    },
    // The two import dialogs opened from the document editor's File > Import menu — bringing outside files and Google Docs in as documents of their own. Kept out of `editor` because they are about getting documents into Yarnboard, not about editing one.
    importDocs: {
      importFailed: 'La importación falló',
      localTitle: 'Importar desde este dispositivo',
      dropzoneTitle: 'Arrastra los archivos aquí, o haz clic para buscarlos',
      supportedFormats: 'Formatos admitidos:',
      selectedFiles: 'Archivos seleccionados',
      importing: 'Importando...',
      importFileOne: 'Importar {count} archivo',
      importFileMany: 'Importar {count} archivos',
      pickerLoadFailed: 'No se pudo cargar Google Picker',
      pickerTitle: 'Elige los Google Docs que quieres importar',
      authCancelled: 'La autorización de Google se canceló o falló',
      googleTitle: 'Importar desde Google Docs',
      loadingPicker: 'Cargando Google Picker…',
      unconfiguredTitle: 'La importación de Google Docs aún no está configurada',
      unconfiguredDesc: 'Esta función necesita una clave de API de Google Picker y un número de proyecto configurados en el servidor.',
      googleHeading: 'Importa tus Google Docs',
      googleDesc: 'Elige los documentos que quieres en el explorador de archivos de Google. Yarnboard solo ve los archivos que elijas — nada más de tu Drive.',
      importingEllipsis: 'Importando…',
      chooseFromDrive: 'Elegir desde Google Drive',
      complete: 'Importación terminada',
      importedOne: 'Se importó {count} de {total} documento.',
      importedMany: 'Se importaron {count} de {total} documentos.',
      imagesSkippedOne: '{count} imagen quedó fuera — Google las sirve desde enlaces que caducan, así que hay que volver a agregarlas aquí.',
      imagesSkippedMany: '{count} imágenes quedaron fuera — Google las sirve desde enlaces que caducan, así que hay que volver a agregarlas aquí.',
      failedOne: 'No se pudo importar {count} documento.',
      failedMany: 'No se pudieron importar {count} documentos.',
      done: 'Listo',
    },
    common: {
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      confirm: 'Confirmar',
      back: 'Volver',
    },
  },
  pt: {
    nav: {
      home: 'Início',
      documents: 'Documentos',
      visualize: 'Visualizar',
      discover: 'Descobrir',
      settings: 'Configurações',
      addNote: 'Adicionar Nota',
      addDocument: 'Adicionar Documento',
      tagManagement: 'Etiquetas',
      importDocuments: 'Importar Documentos',
      upgrade: 'Fazer Upgrade',
      sharedWithMe: 'Compartilhado comigo',
      notes: 'Notas',
      storyboards: 'Storyboards',
      storyboardsTab: 'Storyboards',
      shared: 'Compartilhado',
      about: 'Sobre',
      feedback: 'Feedback',
      addFolder: 'Adicionar Pasta',
      addStoryboard: 'Adicionar Storyboard',
    },
    home: {
      searchPlaceholder: 'Buscar entradas e documentos...',
      loading: 'Carregando...',
      noEntries: 'Nenhuma entrada encontrada',
      noDocuments: 'Nenhum documento encontrado',
      createFirstNote: 'Crie sua primeira nota para começar',
      createFirstDocument: 'Crie seu primeiro documento para começar',
      selectEntries: 'Selecione entradas para criar um documento',
      cancel: 'Cancelar',
      next: 'Avançar',
      selected: 'selecionadas',
      createDocumentFromNotes: 'Criar Documento a partir de Notas',
      newFolder: 'Nova Pasta',
      untitledNote: 'Nota sem título',
      subfolderNamePrompt: 'Nome da subpasta:',
      folderCreateFailed: 'Não foi possível criar a pasta — já pode existir uma com esse nome aqui.',
      searchDocumentsPlaceholder: 'Buscar documentos...',
      searchNotesPlaceholder: 'Buscar notas...',
      includeRelated: 'Incluir notas relacionadas',
      includeRelatedHint: 'Mostrar também as notas ligadas por etiquetas em comum, depois das correspondências diretas',
      createNewFolder: 'Criar pasta nova',
      noNotes: 'Nenhuma nota encontrada',
      tryDifferentSearch: 'Tente outra busca',
      clickAddNote: 'Clique em «Adicionar Nota» para começar',
      relatedNotes: 'Notas relacionadas — ligadas por etiquetas em comum',
      closeMatch: 'Correspondência aproximada',
      matchedTag: 'Etiqueta correspondente',
      connectedBy: 'Ligada por:',
      inPdf: 'No PDF',
      loadingMoreNotes: 'Carregando mais notas...',
      importLocal: 'Este dispositivo',
      importLocalDesc: 'Envie arquivos do seu dispositivo',
      importGoogleDocs: 'Google Docs',
      importGoogleDocsDesc: 'Importar do Google Drive',
      suggestionsTags: 'Etiquetas',
      suggestionsRecent: 'Recentes',
      removeFromHistory: 'Remover {query} do histórico',
      clearSearch: 'Limpar pesquisa',
    },
    note: {
      edit: 'Editar',
      delete: 'Excluir',
      viewImage: 'Ver Imagem',
      tags: 'Etiquetas',
      links: 'Links',
      createdOn: 'Criada em',
      clickToCopy: 'Clique para copiar.',
      clickToOpenPdf: 'Clique para abrir o PDF.',
      openPdf: 'Abrir PDF',
      downloadPdf: 'Baixar PDF',
      seeThreads: 'Veja as conexões',
      viewMore: 'Ver mais',
      close: 'Fechar',
      confirmDelete: 'Tem certeza de que quer excluir esta nota?',
      imageCopied: 'Imagem copiada para a área de transferência.',
      textCopied: 'Texto copiado para a área de transferência.',
      mediaAlt: 'Mídia da nota',
      loadingImage: 'Carregando imagem...',
      noImageFile: 'Sem arquivo de imagem',
      noImageUrl: 'Sem URL de imagem',
      fullSizeAlt: 'Tamanho original',
    },
    document: {
      open: 'Abrir',
      rename: 'Renomear',
      delete: 'Excluir',
      sources: 'fontes',
      lastModified: 'Última modificação',
      createdOn: 'Criado em',
      deleteDocument: 'Excluir documento',
      confirmDeleteDocument: 'Excluir "{name}"? Não dá para desfazer.',
      selectedCount: '{count} selecionados',
      clearSelection: 'Limpar seleção',
      dragSelectionHint: 'Arraste qualquer um deles para uma pasta para mover todos',
      deleteSelected: 'Excluir documentos',
      confirmDeleteSelected: 'Excluir {count} documentos? Não dá para desfazer.',
    },
    folder: {
      createFolder: 'Criar Nova Pasta',
      newFolder: 'Nova Pasta',
      folderName: 'Nome da Pasta',
      enterFolderName: 'Digite o nome da pasta...',
      create: 'Criar Pasta',
      creating: 'Criando...',
      cancel: 'Cancelar',
      delete: 'Excluir',
      deleteFolder: 'Excluir Pasta',
      cannotDeleteWithDocs: 'Não é possível excluir a pasta porque ela contém documentos. Mova ou exclua os documentos primeiro.',
      confirmDelete: 'Tem certeza de que quer excluir a pasta',
      confirmDeleteMessage: 'Esta ação não pode ser desfeita.',
      noDocuments: 'Nenhum documento nesta pasta',
      dragDropHint: 'Arraste e solte documentos aqui para organizá-los',
      expandFolder: 'Expandir pasta',
      collapseFolder: 'Recolher pasta',
      confirmDeleteFolder: 'Excluir a pasta "{name}"? Não dá para desfazer.',
      folderHolds: '"{name}" contém {contents}.',
      documentsMoveToTop: 'Os documentos passam para o nível principal.',
      subfoldersRemoved: 'As subpastas são removidas e seus documentos passam para o nível principal.',
      alsoDeleteDocuments: 'Excluir também {count} que estão dentro',
      alsoDeleteDocumentsHint: 'É permanente — os documentos se perdem para sempre.',
      typeToConfirm: 'Digite {word} para confirmar',
      deleting: 'Excluindo...',
      documentOne: 'documento',
      documentMany: 'documentos',
      subfolderOne: 'subpasta',
      subfolderMany: 'subpastas',
    },
    tagManagement: {
      title: 'Gerenciar Etiquetas',
      searchTags: 'Buscar etiquetas...',
      sortBy: 'Ordenar por',
      nameAZ: 'Nome A-Z',
      nameZA: 'Nome Z-A',
      mostUsed: 'Mais Usadas',
      leastUsed: 'Menos Usadas',
      color: 'Cor',
      textColor: 'Cor do Texto',
      selected: 'selecionadas',
      mergeSelected: 'Mesclar Selecionadas',
      deleteSelected: 'Excluir Selecionadas',
      totalTags: 'Total de Etiquetas',
      showing: 'Mostrando',
      totalEntries: 'Total de Entradas',
      entries: 'entradas',
      edit: 'Editar',
      editTag: 'Editar Etiqueta',
      name: 'Nome',
      tagName: 'Nome da etiqueta',
      saveChanges: 'Salvar Alterações',
      cancel: 'Cancelar',
      mergeTags: 'Mesclar Etiquetas',
      mergeInto: 'Mesclar em',
      confirmMerge: 'Confirmar Mesclagem',
      confirmMergeMessage: 'Isto vai mesclar as seguintes etiquetas',
      cannotUndo: 'Esta ação não pode ser desfeita. Continuar?',
      confirmDelete: 'Confirmar Exclusão',
      confirmDeleteMessage: 'Excluir {count} etiquetas?',
      willRemoveTags: 'Isto vai remover estas etiquetas de {count} entradas.',
      deleteTags: 'Excluir Etiquetas',
      selectAtLeast2: 'Selecione pelo menos 2 etiquetas para mesclar',
      selectTagsToDelete: 'Selecione as etiquetas que quer excluir',
      loadingTags: 'Carregando etiquetas...',
      enterTagName: 'Digite o nome da etiqueta',
      preview: 'prévia',
      failedToUpdate: 'Não foi possível atualizar a etiqueta',
      failedToMerge: 'Não foi possível mesclar as etiquetas',
      failedToDelete: 'Não foi possível excluir as etiquetas',
    },
    settings: {
      title: 'Configurações',
      account: 'Conta',
      appearance: 'Aparência',
      language: 'Idioma',
      help: 'Ajuda',
      about: 'Sobre',
      accountInfo: 'Informações da Conta',
      name: 'Nome',
      displayName: 'Nome de Exibição',
      enterName: 'Digite seu nome',
      updateName: 'Atualizar Nome',
      email: 'E-mail',
      emailLabel: 'E-mail',
      currentEmail: 'E-mail Atual',
      changeEmail: 'Alterar E-mail',
      newEmail: 'Novo E-mail',
      enterNewEmail: 'Digite o novo e-mail',
      updateEmail: 'Atualizar E-mail',
      updating: 'Atualizando...',
      password: 'Senha',
      changePassword: 'Alterar Senha',
      currentPassword: 'Senha Atual',
      enterCurrentPassword: 'Digite a senha atual',
      newPassword: 'Nova Senha',
      enterNewPassword: 'Digite a nova senha',
      confirmPassword: 'Confirmar Senha',
      confirmNewPassword: 'Confirme a nova senha',
      updatePassword: 'Atualizar Senha',
      subscription: 'Assinatura',
      subscriptionPlaceholder: 'Gerenciamento de assinatura em breve.',
      logout: 'Sair',
      logoutButton: 'Sair',
      selectTheme: 'Tema',
      darkTheme: 'Modo Escuro',
      lightTheme: 'Modo Claro',
      selectLanguage: 'Selecione seu idioma preferido',
      english: 'English',
      spanish: 'Español (Latinoamérica)',
      portuguese: 'Português (Brasil)',
      feedbackTitle: 'Enviar feedback',
      feedbackDesc: 'Achou um bug ou tem uma ideia? Envie direto para o desenvolvedor.',
      feedbackTypeBug: 'Bug',
      feedbackTypeIdea: 'Ideia / pedido',
      feedbackTypeOther: 'Outro',
      feedbackPlaceholder: 'Descreva o bug ou a ideia…',
      feedbackSend: 'Enviar feedback',
      feedbackSending: 'Enviando…',
      feedbackThanks: 'Valeu! Seu feedback foi enviado.',
      feedbackError: 'Não foi possível enviar. Tente de novo.',
      helpSupport: 'Ajuda e Suporte',
      searchHelp: 'Buscar artigos de ajuda...',
      searchButton: 'Buscar',
      helpPlaceholder: 'Artigos de ajuda e tutoriais em vídeo em breve.',
      aboutApp: 'Sobre o Yarnboard',
      version: 'Versão 1.0.0',
      aboutDescription: 'O Yarnboard é um aplicativo poderoso de notas e gestão de documentos.',
      aboutPlaceholder: 'Mais informações em breve.',
      dangerZone: 'Zona de Perigo',
      deactivateAccount: 'Desativar Conta',
      deactivateWarning: 'Desativar sua conta vai desabilitá-la temporariamente. Você pode reativá-la fazendo login novamente.',
      deactivate: 'Desativar Conta',
      deleteAccount: 'Excluir Conta',
      deleteWarning: 'Esta ação não pode ser desfeita. Todos os seus dados serão excluídos permanentemente.',
      deleteAccountButton: 'Excluir Conta Permanentemente',
      emailUpdated: 'E-mail atualizado com sucesso',
      nameUpdated: 'Nome atualizado com sucesso',
      passwordUpdated: 'Senha atualizada com sucesso',
      passwordChanged: 'Senha alterada com sucesso',
      themeUpdated: 'Tema atualizado com sucesso',
      languageUpdated: 'Idioma atualizado com sucesso',
      passwordMismatch: 'As novas senhas não conferem',
      passwordTooShort: 'A senha precisa ter pelo menos 8 caracteres',
      updateFailed: 'Falha ao atualizar',
      failedToUpdate: 'Falha ao atualizar',
      failedToLoad: 'Falha ao carregar as configurações',
      failedToChange: 'Falha ao alterar a senha',
      loading: 'Carregando...',
      currentPasswordPrompt: 'Digite sua senha atual para mudar seu e-mail:',
      confirmDeactivate: 'Tem certeza de que quer desativar sua conta? Você pode reativá-la entrando de novo.',
      failedToDeactivate: 'Não foi possível desativar a conta',
      deletePrompt: 'Digite «DELETE» para excluir sua conta e todos os seus dados de forma permanente:',
      failedToDeleteAccount: 'Não foi possível excluir a conta',
      moreThemesSoon: 'Mais temas de cor em breve.',
    },
    addNote: {
      title: 'Adicionar Nota',
      cancel: 'Cancelar',
      remember: 'Lembrar',
      content: 'Conteúdo',
      contentPlaceholder: 'O que você quer lembrar?',
      tags: 'Etiquetas',
      tagsPlaceholder: 'Adicionar etiquetas...',
      links: 'Links',
      addLink: 'Adicionar Link',
      linkUrl: 'URL',
      linkTitle: 'Título (opcional)',
      image: 'Imagem',
      uploadImage: 'Enviar Imagem',
      pasteImage: 'ou cole da área de transferência',
      error: 'Erro',
      aiSuggest: 'Etiquetamento automático',
      generatingTags: 'Gerando etiquetas...',
      tagSuggestionFailed: 'Falha ao gerar etiquetas',
      noTextInImage: 'Nenhum texto encontrado na imagem',
      aiOffNeedsTag: 'Adicione pelo menos uma etiqueta, ou ligue de novo o etiquetamento automático.',
      summarizeVideo: 'Resumir o vídeo do YouTube',
      summarizeVideoDisabled: 'Adicione um link do YouTube para resumi-lo',
      summarizingVideo: 'Assistindo ao vídeo...',
      videoSummaryHeading: 'Resumo do vídeo',
      videoSummaryFailed: 'Não foi possível resumir o vídeo',
      saving: 'Salvando...',
      pasteImageFailed: 'Não foi possível processar a imagem colada',
      loadNoteFailed: 'Não foi possível carregar os dados da nota',
      unsupportedFile: 'Tipo de arquivo não suportado',
      fileFailed: 'Não foi possível processar o arquivo',
      ocrFailed: 'Não foi possível extrair o texto da imagem',
      addContentFirst: 'Escreva algum conteúdo antes de gerar etiquetas',
      contentRequired: 'É preciso conteúdo ou uma imagem.',
      tagRequiredFree: 'Adicione ao menos uma etiqueta para salvar sua nota.',
      tagRequired: 'É preciso ao menos uma etiqueta.',
      saveFailed: 'Não foi possível salvar a nota',
      addAttachment: 'Adicionar imagem, vídeo ou PDF',
      extractText: 'Extrair texto da imagem',
      addSourceUrls: 'Adicionar URLs de origem',
      aiInfo: 'Os resultados da IA podem variar. Para sair melhor, adicione antes algumas etiquetas suas para guiar as sugestões.',
      codeBlockHint: 'Bloco de código (ou digite ```)',
      equation: 'Equação',
      equationHint: 'Equação (ou digite $x^2$)',
      attachedAlt: 'Anexo',
      removeImage: 'Remover imagem',
      removePdf: 'Remover PDF',
      codeLanguage: 'Linguagem do código',
      latexPrompt: 'LaTeX',
      latexInsertPrompt: 'LaTeX (por exemplo E = mc^2)',
    },
    editNote: {
      title: 'Editar Nota',
      update: 'Atualizar',
    },
    addDocument: {
      title: 'Criar Novo Documento',
      documentName: 'Nome do Documento',
      documentNamePlaceholder: 'Nome do Documento',
      create: 'Criar',
      creating: 'Criando...',
      cancel: 'Cancelar',
      nameRequired: 'O nome do documento é obrigatório.',
      failedToCreate: 'Falha ao criar o documento',
    },
    editor: {
      untitled: 'Documento Sem Título',
      saving: 'Salvando...',
      saved: 'Salvo',
      unsaved: 'Alterações não salvas',
      share: 'Compartilhar',
      info: 'Informações do Documento',
      back: 'Voltar',
      convertToEntry: 'Converter documento em entrada única',
      convertToFootnote: 'Converter seleção em nota de rodapé',
      file: 'Arquivo',
      edit: 'Editar',
      insert: 'Inserir',
      format: 'Formatar',
      view: 'Exibir',
      tools: 'Ferramentas',
      help: 'Ajuda',
      print: 'Imprimir',
      undo: 'Desfazer',
      redo: 'Refazer',
      selectAll: 'Selecionar Tudo',
      bold: 'Negrito',
      italic: 'Itálico',
      underline: 'Sublinhado',
      strikethrough: 'Tachado',
      textColor: 'Cor do texto',
      highlightColor: 'Cor do realce',
      fontSize: 'Tamanho da fonte',
      decreaseFontSize: 'Diminuir o tamanho da fonte',
      increaseFontSize: 'Aumentar o tamanho da fonte',
      heading: 'Título',
      heading1: 'Título 1',
      heading2: 'Título 2',
      heading3: 'Título 3',
      heading4: 'Título 4',
      heading5: 'Título 5',
      heading6: 'Título 6',
      paragraph: 'Parágrafo',
      bulletList: 'Lista com marcadores',
      numberedList: 'Lista numerada',
      checkList: 'Lista de tarefas',
      alignLeft: 'Alinhar à esquerda',
      alignCenter: 'Centralizar',
      alignRight: 'Alinhar à direita',
      alignJustify: 'Justificar',
      indent: 'Aumentar recuo',
      outdent: 'Diminuir recuo',
      link: 'Link',
      image: 'Imagem',
      table: 'Tabela',
      horizontalRule: 'Linha horizontal',
      blockquote: 'Citação',
      codeBlock: 'Bloco de código',
      clearFormatting: 'Limpar formatação',
      builtFrom: 'Feito a partir destas notas',
      sourcesChanged: 'Uma nota que deu origem a isto mudou desde então.',
      sourceChanged: 'mudou',
      writerNote: 'Nota para você',
      writerNoteHint: 'Nota para você — não sai em nada que você exportar',
      versionHistory: 'Histórico de versões',
      versionHistoryIntro: 'O Yarnboard guarda as últimas 15 versões deste documento enquanto você escreve, para recuperar o que for apagado ou alterado por engano.',
      versionsLoading: 'Carregando versões…',
      noVersionsYet: 'Ainda não há versões anteriores. Elas são salvas enquanto você escreve.',
      versionWords: 'palavras',
      restoreVersion: 'Restaurar',
      restoreConfirm: 'Substituir o documento por esta versão? O que está aqui agora ficará no histórico, então você pode desfazer isso.',
      restoreFailed: 'Não foi possível restaurar esta versão. Tente novamente.',
      loadingDocument: 'Carregando documento...',
      loadFailed: 'Não foi possível carregar o documento',
      saveFailed: 'Não foi possível salvar o documento',
      backToEntryForm: 'Voltar ao formulário da nota',
      importMenu: 'Importar',
      importLocalComputer: 'Este dispositivo',
      download: 'Baixar',
      downloadPdf: 'PDF (.pdf)',
      downloadWord: 'Word (.docx)',
      downloadRichText: 'Texto formatado (.rtf)',
      downloadWebPage: 'Página web (.html)',
      cut: 'Recortar',
      copy: 'Copiar',
      paste: 'Colar',
      withoutFormatting: '(sem formatação)',
      showRuler: 'Mostrar régua',
      printLayout: 'Layout de impressão',
      fullScreen: 'Tela cheia',
      text: 'Texto',
      superscript: 'Sobrescrito',
      subscript: 'Subscrito',
      upperCase: 'MAIÚSCULAS',
      lowerCase: 'minúsculas',
      titleCase: 'Tipo Título',
      alignAndIndent: 'Alinhar e recuar',
      left: 'Esquerda',
      center: 'Centro',
      right: 'Direita',
      justify: 'Justificar',
      increaseIndent: 'Aumentar recuo',
      decreaseIndent: 'Diminuir recuo',
      script: 'Roteiro',
      scriptScene: 'Cabeçalho de cena',
      scriptAction: 'Ação',
      scriptCharacter: 'Personagem',
      scriptDialogue: 'Diálogo',
      scriptParenthetical: 'Parêntese',
      scriptTransition: 'Transição',
      scriptNone: 'Texto normal',
      scriptHint: 'Tab alterna os elementos · Enter segue',
      scriptHintNote: '(personagem→diálogo…)',
      bulletsAndNumbering: 'Marcadores e numeração',
      lineAndSpacing: 'Linha e espaçamento',
      spacingSingle: 'Simples',
      spacingDouble: 'Duplo',
      addSpaceBefore: 'Adicionar espaço antes do parágrafo',
      removeSpaceBefore: 'Remover espaço antes do parágrafo',
      customSpacingMenu: 'Espaçamento personalizado...',
      keepWithNext: 'Manter com o próximo',
      keepLinesTogether: 'Manter as linhas juntas',
      preventSingleLines: 'Evitar linhas soltas',
      addPageBreakBefore: 'Adicionar quebra de página antes',
      insertRowAbove: 'Inserir linha acima',
      insertRowBelow: 'Inserir linha abaixo',
      insertColumnLeft: 'Inserir coluna à esquerda',
      insertColumnRight: 'Inserir coluna à direita',
      toggleHeaderRow: 'Alternar linha de cabeçalho',
      deleteRow: 'Excluir linha',
      deleteColumn: 'Excluir coluna',
      deleteTable: 'Excluir tabela',
      wordCount: 'Contagem de palavras',
      proofreadSettings: 'Configurações de revisão',
      infoSourceEntries: 'Notas de origem:',
      infoTags: 'Etiquetas:',
      infoLinks: 'Links:',
      tags: 'Etiquetas',
      links: 'Links',
      textStyle: 'Estilo do texto',
      screenplayElementHint: 'Elemento de roteiro — Tab alterna, Enter passa ao próximo',
      notScript: 'Sem roteiro',
      font: 'Fonte',
      defaultColor: 'Cor padrão',
      noHighlight: 'Sem realce',
      textOutline: 'Contorno do texto',
      themeDefault: 'Cor do tema',
      removeOutline: 'Remover contorno',
      insertImageOrPaste: 'Inserir imagem (ou colar)',
      customColor: 'Cor personalizada',
      custom: 'Personalizada',
      pageSingle: 'Página 1',
      pageRange: 'Páginas 1–{count}',
      pages: 'Páginas',
      words: 'Palavras',
      characters: 'Caracteres',
      charactersNoSpaces: 'Caracteres sem espaços',
      displayWordCountWhileTyping: 'Mostrar a contagem de palavras enquanto você escreve',
      wordsLabel: 'Palavras:',
      proofreadIntro: 'Escolha quais revisões ficam ativas enquanto você escreve:',
      proofSpelling: 'Ortografia',
      proofSpellingDesc: 'Sublinha em vermelho as palavras erradas',
      proofGrammar: 'Gramática',
      proofGrammarDesc: 'Sublinha em azul os problemas de gramática',
      proofActiveVoice: 'Voz ativa',
      proofActiveVoiceDesc: 'Detecta a voz passiva e sugere alternativas na voz ativa',
      proofConciseness: 'Concisão',
      proofConcisenessDesc: 'Sugere alternativas mais curtas e corta o excesso de palavras',
      proofSentenceSplit: 'Divisão de frases',
      proofSentenceSplitDesc: 'Identifica as frases complexas demais',
      noteLabel: 'Nota:',
      proofreadNote: 'A ortografia é verificada em português do Brasil, espanhol e inglês, de acordo com o idioma de cada parágrafo. Clique com o botão direito numa palavra sublinhada para ver sugestões ou adicioná-la ao seu dicionário.',
      spellAddToDictionary: 'Adicionar ao dicionário',
      spellIgnore: 'Ignorar',
      spellNoSuggestions: 'Sem sugestões',
      customSpacing: 'Espaçamento personalizado',
      lineSpacing: 'Espaçamento entre linhas',
      paragraphSpacingPts: 'Espaçamento do parágrafo (pts)',
      spacingBefore: 'Antes',
      spacingAfter: 'Depois',
      apply: 'Aplicar',
      placeholder: 'Comece a escrever seu documento...',
      clipboardBlockedCut: 'O navegador bloqueou o recortar — use ⌘X / Ctrl+X.',
      clipboardBlockedCopy: 'O navegador bloqueou o copiar — use ⌘C / Ctrl+C.',
      clipboardBlockedPaste: 'O navegador bloqueou o colar — use ⌘V / Ctrl+V.',
      linkUrlPrompt: 'URL do link (deixe vazio para remover o link):',
      imagePasteFailed: 'Não foi possível enviar a imagem colada. Tente novamente.',
      imageUploadFailed: 'Não foi possível enviar a imagem. Tente novamente.',
      exportFailed: 'A exportação falhou. Tente novamente.',
    },
    orgDoc: {
      title: 'Organizar Documento',
      documentName: 'Nome do Documento',
      namePlaceholder: 'Digite o nome do documento...',
      saving: 'Salvando...',
      instructions: 'Arraste as entradas para reordená-las',
      entries: 'Entradas',
      preview: 'Prévia',
      stats: 'Estatísticas do Documento',
      totalEntries: 'Total de Entradas',
      totalTags: 'Total de Etiquetas',
      totalLinks: 'Total de Links',
      combinedTags: 'Etiquetas Combinadas',
      combinedLinks: 'Links Combinados',
      contentPreview: 'Prévia do Conteúdo',
      cancel: 'Cancelar',
      finalize: 'Finalizar Documento',
    },
    sharing: {
      share: 'Compartilhar',
      shareTitle: 'Compartilhar “{name}”',
      peopleWithAccess: 'Pessoas com acesso',
      onlyYou: 'Por enquanto só você tem acesso.',
      inviteByEmail: 'Convidar por e-mail',
      emailPlaceholder: 'nome@exemplo.com',
      invite: 'Convidar',
      inviting: 'Convidando…',
      canEdit: 'Pode editar',
      canView: 'Pode ver',
      pending: 'Pendente',
      pendingExplain: 'Pendente quer dizer que a pessoa ainda não abriu o convite. O acesso começa quando ela abre o link e entra na conta.',
      emailedLink: 'Enviamos o link para ela por e-mail.',
      sendThemLink: 'Mande este link para ela.',
      copyLink: 'Copiar link',
      copied: 'Copiado',
      remove: 'Remover',
      removeConfirm: 'Parar de compartilhar com {name}?',
      loadFailed: 'Não foi possível carregar quem tem acesso.',
      close: 'Fechar',
      sharedBy: 'Compartilhado por {name}',
      youCanEdit: 'Você pode editar',
      youCanView: 'Você pode ver',
      lastEditedBy: 'Última edição de {name}',
      viewOnly: 'Somente leitura',
      othersNote: 'Nota de {name}',
      sharedWithMeTitle: 'Compartilhado comigo',
      nothingShared: 'Ninguém compartilhou nada com você ainda.',
      kindNote: 'Notas',
      kindDocument: 'Documentos',
      kindStoryboard: 'Storyboards',
      updated: 'Atualizado em {date}',
      leave: 'Sair',
      leaveConfirm: 'Sair de “{name}”? Você perde o acesso até compartilharem de novo com você.',
      invitedYouEdit: '{name} convidou você para editar “{title}”',
      invitedYouView: '{name} convidou você para ver “{title}”',
      inviteSignIn: 'Entrar',
      inviteCreateAccount: 'Criar conta',
      inviteAccept: 'Aceitar convite',
      inviteAccepting: 'Abrindo…',
      inviteSignedOutHint: 'Entre com {email}, ou crie uma conta com esse e-mail, e você volta direto para cá.',
      inviteUsed: 'Este convite já foi usado por outra pessoa.',
      inviteInvalid: 'Este convite não vale mais.',
      inviteFailed: 'Não foi possível aceitar este convite.',
      conflictTitle: 'Outra pessoa salvou primeiro',
      conflictBody: '{name} salvou uma versão mais nova enquanto você escrevia.',
      conflictLoadTheirs: 'Carregar a versão dela',
      conflictKeepMine: 'Manter a minha',
      conflictWarning: 'Carregar a versão dela substitui o que está na sua tela. Manter a sua salva por cima da dela.',
    },
    // The document explorer's toolbar and folder rows
    explorer: {
      newSubfolder: 'Nova subpasta',
      newDocumentInFolder: 'Novo documento nesta pasta',
      name: 'Nome',
      modified: 'Modificado',
      created: 'Criado',
      grouping: 'Agrupamento',
      foldersFirst: 'Pastas primeiro',
      filesFirst: 'Arquivos primeiro',
      mixed: 'Misturado',
    },
    // The tag field on the note composer
    tagInput: {
      label: 'Etiquetas',
      required: '· obrigatório',
      editBackgroundColor: 'Editar cor de fundo',
      editTextColor: 'Editar cor do texto',
      removeTag: 'Remover etiqueta',
      addAnother: 'Adicionar outra…',
      addTags: 'Adicione etiquetas… aperte Enter',
      recentClickToAdd: 'Recentes · clique para adicionar',
    },
    // Source and external links, on the note composer and the note form
    links: {
      sourceUrls: 'URLs de origem',
      externalLinks: 'Links Externos',
      enterUrl: 'Digite a URL...',
      typeReference: 'Referência',
      typeVideo: 'Vídeo',
      typeMeme: 'Meme',
      add: 'Adicionar',
      removeLink: 'Remover link',
      collapseSection: 'Recolher seção',
    },
    // The in-app About page, including its privacy policy. Bodies use **bold** and *italic* markers, rendered by About.tsx.
    about: {
      tagline: 'Ligue. Os. Pontos.',
      whatIsTitle: 'O que é essa coisa?',
      whatIsP1: 'Você conhece aquela sensação de *saber* que viu alguma coisa — um meme, um tuíte, aquele vídeo — e não conseguir achar? Lá vai você, rolando meses de caos para remontar **aquele argumento perfeito** que tinha guardado na cabeça.',
      whatIsP2: '**Boas-vindas ao Yarnboard.** Isto não é mais um aplicativo de notas. É o seu *mural de conspiração digital*. Com barbante vermelho e tudo. Só que o barbante vermelho é mesmo inteligente, os alfinetes dá para pesquisar e nada cai no chão às 3 da manhã.',
      whoForTitle: 'Para quem é isto?',
      whoForIntro: 'Para você. Você mesmo. Principalmente se você for:',
      whoFor1: '**Jornalista investigativo** seguindo fontes por uma dúzia de tocas de coelho',
      whoFor2: '**Documentarista** se afogando em marcações de tempo e citações de entrevistas',
      whoFor3: '**Escritor** que se recusa a perder aquela frase perfeita que pensou no banho',
      whoFor4: '**Pesquisador** organizando citações como quem defende uma tese diante de uma banca especialmente hostil',
      whoFor5: '**Youtuber** que precisa citar as fontes antes que alguém te corrija nos comentários',
      whoFor6: '**Professor** montando aulas com PDFs soltos e artigos salvos nos favoritos',
      whoFor7: '**Comediante** guardando premissas de piadas e aquela resposta perfeita para depois',
      whoFor8: '**Alguém que ensina a ler mídia** rastreando desinformação como quem caça o Pé Grande (só que o Pé Grande existe e se chama desinformação)',
      whoFor9: '**Viciado em Twitter/Bluesky** que precisa lembrar na hora daquela thread de três semanas atrás',
      whoFor10: '**Advogado** que *definitivamente* não quer perder uma prova de vista',
      whoFor11: '**Treinador** organizando material de treino e frases motivacionais',
      whoFor12: '**Qualquer um** que já disse «juro que vi isso em algum lugar…»',
      howTitle: 'A conspiração (como funciona)',
      howIntro: 'Imagine um **mural de barbante literal** — daqueles em que o detetive prega fotos e liga tudo com barbante vermelho. Agora imagine que esse mural é *infinito*, que o barbante se amarra sozinho e que dá para buscar nele inteiro digitando três letras.',
      featBrainTitle: '🧠 Seu cérebro externo',
      featBrainBody: 'Cada meme. Cada print. Cada momento de «isso eu preciso lembrar». Guarde aqui. O OCR lê o texto das imagens, então você acha aquele print de tuíte de 2022 digitando «clima» ou «ratio» ou seja lá como se fala agora.',
      featTagsTitle: '🏷️ Tudo ganha etiqueta',
      featTagsBody: 'Sem etiqueta? Sem entrada. Não vamos deixar você virar um acumulador digital. Tudo precisa de pelo menos uma etiqueta. A IA etiqueta sozinha, porque ela realmente *lê* o que você está guardando. Seu eu do futuro agradece quando achar aquilo em 2 segundos em vez de 2 horas.',
      featWebTitle: '🕸️ A teia de conexões',
      featWebBody: 'Um mapa mental visual que desenha conexões que você nem sabia que existiam. Aquela citação de podcast? Tem a ver com estes três artigos, aquele documentário e esta thread. **Os pontos se ligam sozinhos**. De nada.',
      featRecallTitle: '🎯 Memória instantânea',
      featRecallBody: '«Isso eu vivo repetindo nos comentários…» Busque, toque, copie, cole. Pronto. A citação que você precisa? A resposta perfeita? O link do vídeo que te pediram? Está aqui. Sempre.',
      featVideoTitle: '📹 Vídeo? Transcrito.',
      featVideoBody: 'Cole um link do YouTube. O Yarnboard tira o áudio, transcreve e deixa tudo pesquisável. Aí, quando alguém perguntar «o que era aquilo do vídeo?», você responde de verdade em vez de dizer «era… ahn… depois eu acho» (você não vai achar).',
      featVoiceTitle: '🎙️ Notas de voz que não somem',
      featVoiceBody: 'Grave notas de voz. Elas são transcritas e guardadas. Porque às vezes as melhores ideias chegam dirigindo e não dá para anotar sem causar um acidente.',
      featDocsTitle: '📄 Documentos a partir do caos',
      featDocsBody: 'Junte entradas soltas, busque no seu despejo mental e monte documentos de verdade. Divida as entradas quando ficarem amplas demais. Junte quando ficarem específicas demais. Arraste, solte, edite, reorganize. É tipo LEGO, mas de informação.',
      featSourcesTitle: '🔗 Todas as suas fontes, organizadas',
      featSourcesBody: 'Guarde links ilimitados em cada entrada. Citações? Referências? «Onde eu vi isso?» Está tudo lá. Advogados e pesquisadores: isto vai servir *especialmente* para vocês.',
      featImportTitle: '📥 Importe tudo',
      featImportBody: 'Já tem documentos prontos? PDFs? Google Docs? Jogue aqui. A IA lê, etiqueta e integra tudo à sua teia de conhecimento. Suas notas antigas não são mais inúteis: agora estão *conectadas*.',
      featExtensionTitle: '🌐 Em breve: extensão de navegador',
      featExtensionBody: 'Viu algo interessante? Um clique. Salvo. Etiquetado. Conectado. É tipo recortar a web, mas para quem de fato *usa* os recortes.',
      bottomTitle: 'Resumo da ópera',
      bottomP1: 'O Yarnboard não é só um aplicativo. É um **mural de conspiração para a sua cabeça**. Tudo o que você vê, lê, assiste ou pensa fica guardado, organizado, conectado e pesquisável na hora. Quando precisar achar aquilo, você *vai* achar.',
      bottomP2: 'Pare de perder suas ideias. Comece a ligar os pontos.',
      bottomCta: '**Boas-vindas à conspiração. Agora o detetive é você.**',
      privacyTitle: 'Política de Privacidade',
      privacyUpdated: 'Última atualização: 8 de novembro de 2025',
      infoCollectTitle: 'Informações que coletamos',
      infoCollectIntro: 'O Yarnboard coleta e guarda as informações que você escolhe explicitamente salvar no aplicativo, incluindo:',
      infoCollect1: 'Entradas de texto, notas e documentos que você cria',
      infoCollect2: 'Imagens, prints e arquivos de mídia que você envia',
      infoCollect3: 'Gravações de áudio e suas transcrições',
      infoCollect4: 'URLs e links da web que você salva',
      infoCollect5: 'Etiquetas e rótulos de organização que você cria',
      infoCollect6: 'Links de vídeo e suas transcrições',
      infoCollect7: 'Os dados da sua conta (e-mail, nome de usuário, senha)',
      howUseTitle: 'Como usamos suas informações',
      howUseIntro: 'Seus dados são usados apenas para o Yarnboard funcionar:',
      howUse1: 'Guardar e organizar o conteúdo que você salva',
      howUse2: 'Permitir buscar e recuperar suas entradas',
      howUse3: 'Criar conexões visuais entre conteúdos relacionados',
      howUse4: 'Processar OCR em imagens e transcrever áudio e vídeo',
      howUse5: 'Gerar sugestões de etiquetas com IA',
      storageTitle: 'Armazenamento e segurança dos dados',
      storageBody: 'Seu conteúdo é guardado com segurança em servidores na nuvem, com criptografia em trânsito e em repouso. Aplicamos medidas de segurança padrão do setor para proteger seus dados contra acesso, alteração ou exclusão não autorizados.',
      thirdPartyTitle: 'Serviços de terceiros',
      thirdPartyIntro: 'O Yarnboard pode usar serviços de terceiros para:',
      thirdParty1: 'Armazenamento e hospedagem na nuvem',
      thirdParty2: 'Processamento de OCR e transcrição',
      thirdParty3: 'Análise de conteúdo com IA para o etiquetamento',
      thirdPartyOutro: 'Esses serviços seguem suas próprias políticas de privacidade e são escolhidos pelos seus padrões de segurança e privacidade.',
      sharingTitle: 'Compartilhamento de dados',
      sharingBody: '**Não vendemos, alugamos nem compartilhamos seus dados pessoais com terceiros para fins de marketing.** Seu conteúdo continua privado e só você acessa, pela sua conta.',
      rightsTitle: 'Seus direitos',
      rightsIntro: 'Você tem direito a:',
      rights1: 'Acessar todos os dados associados à sua conta',
      rights2: 'Exportar seus dados em formatos padrão',
      rights3: 'Excluir seus dados e sua conta quando quiser',
      rights4: 'Pedir a correção dos seus dados',
      retentionTitle: 'Retenção de dados',
      retentionBody: 'Guardamos seus dados enquanto sua conta estiver ativa. Se você excluir a conta, todos os dados associados são apagados em definitivo em até 30 dias, salvo quando a lei exigir a guarda.',
      cookiesTitle: 'Cookies e rastreamento',
      cookiesBody: 'O Yarnboard usa cookies essenciais para autenticação e gestão de sessão. Não usamos cookies de rastreamento nem cookies de publicidade de terceiros.',
      changesTitle: 'Mudanças nesta política',
      changesBody: 'Podemos atualizar esta política de privacidade de tempos em tempos. Mudanças relevantes são comunicadas no aplicativo ou por e-mail. Continuar usando o Yarnboard depois de uma mudança significa aceitar a política atualizada.',
      contactTitle: 'Contato',
      contactBody: 'Se tiver dúvidas sobre esta política de privacidade ou sobre seus dados, fale com a gente pelos canais de suporte do aplicativo.',
      gdprTitle: 'Conformidade com o GDPR',
      gdprBody: 'Para usuários da União Europeia, o Yarnboard cumpre os requisitos do GDPR. Você tem direito à portabilidade dos dados, ao esquecimento e à limitação do tratamento dos seus dados pessoais.',
      ccpaTitle: 'Conformidade com a CCPA',
      ccpaBody: 'Para residentes da Califórnia, o Yarnboard cumpre a Lei de Privacidade do Consumidor da Califórnia (CCPA). Você tem direito a saber que informações pessoais são coletadas, a pedir a exclusão delas e a se opor à sua venda (ainda que não vendamos informações pessoais).',
      copyright: '© 2025 Yarnboard. Todos os direitos reservados.',
      version: 'Versão 1.0 (codinome: Barbante Vermelho)',
    },
    // The "here's what you'd get" teaser a free user sees at a premium feature
    premium: {
      ocrTitle: 'Extraia o texto de qualquer imagem',
      ocrTagline: 'Tire uma foto de uma página, de um quadro ou de um recibo — o Yarnboard transforma tudo em texto editável.',
      ocrBullet1: 'Transforme capturas e fotos em notas que dá para buscar',
      ocrBullet2: 'Ótimo para páginas escritas à mão e documentos impressos',
      ocrBullet3: 'Um toque na imagem — o texto vai direto para a sua nota',
      aiTaggingTitle: 'Deixe a IA etiquetar suas notas',
      aiTaggingTagline: 'O Yarnboard lê sua nota e sugere as etiquetas certas sozinho.',
      aiTaggingBullet1: 'Etiquetas consistentes sem trabalho repetitivo',
      aiTaggingBullet2: 'Reaproveita as etiquetas que você já tem quando elas servem',
      aiTaggingBullet3: 'Mantém seu quadro organizado enquanto ele cresce',
      discoveryTitle: 'Explore suas notas como um grafo vivo',
      discoveryTagline: 'Escolha qualquer nota e veja as relacionadas se ramificarem — aparecem conexões que você nem lembrava.',
      discoveryBullet1: 'As etiquetas em comum mais raras revelam as ligações mais surpreendentes',
      discoveryBullet2: 'Clique em qualquer nó para ramificar mais',
      discoveryBullet3: 'Transforme uma pilha de notas num mapa do seu pensamento',
      videoSummaryTitle: 'Resuma um vídeo do YouTube dentro da sua nota',
      videoSummaryTagline: 'Salve o link, aperte o botão e os pontos do vídeo caem na sua nota como texto que dá para buscar.',
      videoSummaryBullet1: 'Todos os pontos principais, na ordem em que o vídeo apresenta',
      videoSummaryBullet2: 'Nomes, números e datas ficam — é o que você vai buscar depois',
      videoSummaryBullet3: 'O resumo é texto de nota: edite, etiquete, coloque num documento',
      importTitle: 'Importe os documentos que você já tem',
      importTagline: 'Traga Google Docs e arquivos do seu dispositivo (Word, PDF, Markdown) como documentos editáveis do Yarnboard.',
      importBullet1: 'Escolha Google Docs direto do Drive',
      importBullet2: 'Envie Word, PDF, RTF, Markdown e mais',
      importBullet3: 'Tudo fica totalmente editável no Yarnboard',
      collaborationTitle: 'Escrevam juntos',
      collaborationTagline: 'Compartilhe um storyboard, um documento ou uma nota só com quem trabalha com você.',
      collaborationBullet1: 'Convide qualquer pessoa por e-mail, para editar ou só para ver',
      collaborationBullet2: 'As notas dela ficam no seu quadro ao lado das suas',
      collaborationBullet3: 'Duas pessoas no mesmo documento nunca sobrescrevem uma à outra em silêncio',
      premiumFeature: 'Recurso premium',
      seePlans: 'Ver planos e assinar',
      maybeLater: 'Talvez depois',
    },
    // The Discover view, its free-plan teaser and the note graph
    discover: {
      teaserTitle: 'Explore suas notas como um grafo vivo',
      teaserBody: 'Escolha qualquer nota e veja as notas relacionadas se ramificarem, trazendo à tona conexões que você tinha esquecido. As etiquetas compartilhadas mais raras revelam os laços mais surpreendentes, e cada nó se expande ainda mais.',
      teaserLock: '🔒 Recurso premium',
      teaserCta: 'Ver planos e fazer upgrade',
      title: 'Descubra conexões',
      hint: 'Escolha uma nota para ver o que se relaciona',
      noTaggedNotes: 'Nenhuma nota com etiquetas encontrada',
      loadMore: 'Carregar mais',
      noText: '(sem texto)',
      placeholderTitle: 'Descubra conexões escondidas',
      placeholderBody: 'Escolha uma nota no painel da esquerda. Ela vira o centro de um grafo vivo: as notas relacionadas se ramificam a partir dela, e clicar em qualquer nó ramifica ainda mais.',
      placeholderSub: 'As conexões vêm das etiquetas compartilhadas. Quanto menos notas uma etiqueta tiver, mais forte é o sinal de conexão.',
      graphClickToBranch: 'Clique para ramificar',
      graphEmpty: 'As etiquetas desta nota são só dela — nenhuma outra nota as compartilha ainda.',
      graphHint: 'Clique em um nó para ramificar · arraste para reorganizar · role para dar zoom',
    },
    // The storyboard list and the storyboard editor
    storyboards: {
      title: 'Storyboards',
      newStoryboard: 'Novo storyboard',
      loading: 'Carregando storyboards...',
      noneYet: 'Nenhum storyboard ainda',
      createFirst: 'Crie seu primeiro storyboard para começar a organizar suas entradas de forma visual',
      createStoryboard: 'Criar storyboard',
      createTitle: 'Criar novo storyboard',
      name: 'Nome',
      namePlaceholder: 'Digite o nome do storyboard...',
      descriptionOptional: 'Descrição (opcional)',
      descriptionPlaceholder: 'Digite uma descrição...',
      create: 'Criar',
      entryOne: 'entrada',
      entryMany: 'entradas',
      updated: 'Atualizado em {date}',
      loadFailed: 'Não foi possível carregar os storyboards',
      nameRequired: 'O nome do storyboard é obrigatório',
      createFailed: 'Não foi possível criar o storyboard',
      deleteConfirm: 'Tem certeza de que quer excluir este storyboard?',
      deleteFailed: 'Não foi possível excluir o storyboard',
      loadingOne: 'Carregando storyboard...',
      loadOneFailed: 'Não foi possível carregar o storyboard',
      docCreateFailed: 'Não foi possível criar o documento a partir do storyboard',
      noteSaveFailed: 'Não foi possível salvar essa nota',
      addEntryFailed: 'Não foi possível adicionar a entrada',
      removeEntryFailed: 'Não foi possível remover a entrada',
      orderSaveFailed: 'Não foi possível salvar a ordem',
      removeEntryConfirm: 'Remover esta entrada do storyboard?',
      createDocument: 'Criar documento',
      createDocumentTooltip: 'Combine as entradas deste storyboard, em ordem, em um documento',
      addEntries: 'Adicionar entradas',
      expand: 'Expandir',
      collapse: 'Recolher',
      searchPlaceholder: 'Busque entradas por conteúdo ou etiquetas...',
      newNoteTagged: 'Nova nota com a etiqueta “{tag}”',
      searching: 'Buscando...',
      noContent: '(sem conteúdo)',
      addToStoryboard: 'Adicionar ao storyboard',
      searchPrompt: 'Busque entradas para adicionar ao seu storyboard',
      searchPromptHint: 'Não tem nenhuma? Adicione algumas aqui e depois busque por elas pela etiqueta que você deu.',
      boardHeading: 'Storyboard',
      empty: 'Seu storyboard está vazio',
      emptyHint: 'Busque e adicione entradas para começar',
      cardNotePlaceholder: 'Para que serve este momento?',
      cardNoteTitle: 'Uma nota para você sobre este momento. Ela encabeça este cartão no rascunho.',
      addCardNote: '+ nota',
      docDialogTitle: 'Criar documento a partir do storyboard',
      docDialogHint: 'As {count} entradas serão combinadas na ordem do storyboard.',
      docNamePlaceholder: 'Nome do documento',
      creating: 'Criando…',
      createAndOpen: 'Criar e abrir',
    },
    // Subscription, usage limits and checkout
    billing: {
      planFree: 'Grátis',
      planMonthly: 'Mensal ({price}/mês)',
      planYearly: 'Anual ({price}/ano)',
      planComp: 'Cortesia (acesso total)',
      premiumBadge: 'PREMIUM',
      unlimited: 'ilimitado',
      loadFailed: 'Não foi possível carregar os dados da assinatura',
      loading: 'Carregando…',
      cancelConfirm: 'Cancelar sua assinatura? Você mantém o acesso premium até o fim do período que já pagou.',
      cancelFailed: 'Não foi possível cancelar',
      canceling: 'Cancelando…',
      cancelSubscription: 'Cancelar assinatura',
      pastDueWarning: 'A última renovação falhou — atualize seu pagamento ou o acesso termina em {date}.',
      canceledWarning: 'Cancelada — premium até {date}.',
      switchToYearlyConfirm: 'Mudar para o plano Anual ({price}/ano — economize {saving}%)? Seu plano mensal é cancelado e o anual começa agora.',
      switchToMonthlyConfirm: 'Mudar para o plano Mensal ({price}/mês)? Seu plano anual é cancelado (o acesso vale até o que você já pagou) e a cobrança mensal começa agora.',
      switchToYearly: 'Mudar para Anual (economize {saving}%)',
      switchToMonthly: 'Mudar para Mensal',
      usageNotes: 'Notas',
      usageDocuments: 'Documentos',
      usageStoryboards: 'Storyboards',
      usageStorage: 'Armazenamento',
      upgradeMonthly: 'Fazer upgrade — {price}/mês',
      upgradeYearly: 'Fazer upgrade — {price}/ano (economize {saving}%)',
      checkoutPlanMonthly: 'Mensal',
      checkoutPlanYearly: 'Anual',
      upgradeHeading: 'Faça upgrade para o Yarnboard {plan}',
      priceMonthly: '{price} / mês',
      priceYearly: '{price} / ano ({monthly}/mês)',
      preparingCheckout: 'Preparando o pagamento seguro…',
      confirmingSubscription: 'Confirmando sua assinatura…',
      trialStarted: 'Seu teste grátis de 30 dias começou! Notas e documentos ilimitados, etiquetamento com IA, Descoberta e importações estão liberados. Você só é cobrado quando o teste terminar — cancele quando quiser antes disso em Configurações.',
      done: 'Pronto',
      paymentFormLoadFailed: 'Não foi possível carregar o formulário de pagamento',
      checkoutStartFailed: 'Não foi possível iniciar o pagamento',
      paymentFailed: 'O pagamento falhou. Nada foi cobrado no seu cartão.',
      paymentNotConfirmed: 'Pagamento recebido, mas ainda não confirmado — isso pode levar um instante. Confira em Configurações daqui a pouco.',
      confirmationFailed: 'O pagamento passou, mas a confirmação falhou — confira em Configurações em um minuto.',
    },
    // The two import dialogs opened from the document editor's File > Import menu — bringing outside files and Google Docs in as documents of their own. Kept out of `editor` because they are about getting documents into Yarnboard, not about editing one.
    importDocs: {
      importFailed: 'A importação falhou',
      localTitle: 'Importar deste dispositivo',
      dropzoneTitle: 'Arraste os arquivos até aqui, ou clique para procurar',
      supportedFormats: 'Formatos aceitos:',
      selectedFiles: 'Arquivos selecionados',
      importing: 'Importando...',
      importFileOne: 'Importar {count} arquivo',
      importFileMany: 'Importar {count} arquivos',
      pickerLoadFailed: 'Não foi possível carregar o Google Picker',
      pickerTitle: 'Escolha os Google Docs para importar',
      authCancelled: 'A autorização do Google foi cancelada ou falhou',
      googleTitle: 'Importar do Google Docs',
      loadingPicker: 'Carregando o Google Picker…',
      unconfiguredTitle: 'A importação do Google Docs ainda não está configurada',
      unconfiguredDesc: 'Este recurso precisa de uma chave de API do Google Picker e do número do projeto configurados no servidor.',
      googleHeading: 'Importe seus Google Docs',
      googleDesc: 'Escolha os documentos que você quer no navegador de arquivos do Google. O Yarnboard só vê os arquivos que você escolher — nada mais do seu Drive.',
      importingEllipsis: 'Importando…',
      chooseFromDrive: 'Escolher no Google Drive',
      complete: 'Importação concluída',
      importedOne: 'Importado {count} de {total} documento.',
      importedMany: 'Importados {count} de {total} documentos.',
      imagesSkippedOne: '{count} imagem ficou de fora — o Google as serve por links que expiram, então elas precisam ser adicionadas de novo aqui.',
      imagesSkippedMany: '{count} imagens ficaram de fora — o Google as serve por links que expiram, então elas precisam ser adicionadas de novo aqui.',
      failedOne: 'Não foi possível importar {count} documento.',
      failedMany: 'Não foi possível importar {count} documentos.',
      done: 'Pronto',
    },
    common: {
      save: 'Salvar',
      cancel: 'Cancelar',
      delete: 'Excluir',
      edit: 'Editar',
      close: 'Fechar',
      loading: 'Carregando...',
      error: 'Erro',
      success: 'Sucesso',
      confirm: 'Confirmar',
      back: 'Voltar',
    },
  },
};

export default translations;
